import React, { useMemo, useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import ReactApexChart from "react-apexcharts";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { registerLocale } from "react-datepicker";
import ptBR from "date-fns/locale/pt-BR";
import * as XLSX from "xlsx-js-style";
import {
  Badge,
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "flowbite-react";
import {
  HiSparkles,
  HiChartBar,
  HiShieldCheck,
  HiArrowPath,
  HiArrowTopRightOnSquare,
} from "react-icons/hi2";
import { HiOutlineSparkles, HiOutlineShieldCheck } from "react-icons/hi";
import { useQuery } from "react-query";
import axiosInstance from "../config/axiosInstance";
import { DASHBOARD, CRM } from "../config/apiConfig";
import { useApplication } from "../contexts/ApplicationContext";

registerLocale("pt-BR", ptBR);

const pad2 = (value) => String(value).padStart(2, "0");

const formatDateKey = (date) =>
  `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;

const buildDefaultDateRange = () => {
  const today = new Date();
  const end = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const start = new Date(today.getFullYear(), today.getMonth(), 1);
  return [start, end];
};

const currency = (value) =>
  (Number(value) || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    maximumFractionDigits: 0,
  });

const formatDecisionDate = (value) =>
  value
    ? new Date(value).toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
    : "—";

const formatGeneratedAtLocal = (value) => {
  const time = toTimestamp(value);
  const date = time != null ? new Date(time) : new Date();
  return date.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

const toNumber = (value) => {
  if (value == null) {
    return null;
  }
  const numeric = typeof value === "number" ? value : Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const pickNumericValue = (...values) => {
  for (const value of values) {
    const numeric = toNumber(value);
    if (numeric != null) {
      return numeric;
    }
  }
  return null;
};

const toTimestamp = (value) => {
  if (value == null) {
    return null;
  }
  if (value instanceof Date) {
    const time = value.getTime();
    return Number.isFinite(time) ? time : null;
  }
  if (typeof value === "number") {
    if (!Number.isFinite(value)) {
      return null;
    }
    return value < 1e12 ? value * 1000 : value;
  }
  const parsed = new Date(value);
  const time = parsed.getTime();
  return Number.isFinite(time) ? time : null;
};

const pickDateValue = (source, paths = []) => {
  if (!source) {
    return null;
  }
  for (const path of paths) {
    const value = getFieldValue(source, path);
    if (value != null && value !== "") {
      return value;
    }
  }
  return null;
};

const isWithinRange = (value, startTime, endTime) => {
  const time = toTimestamp(value);
  if (time == null) {
    return false;
  }
  if (startTime != null && time < startTime) {
    return false;
  }
  if (endTime != null && time > endTime) {
    return false;
  }
  return true;
};

const formatCurrencyOrPlaceholder = (value) =>
  value != null ? currency(value) : "—";

const formatInstitutionsList = (institutions = []) => {
  const cleanInstitutions = Array.from(new Set(institutions.filter(Boolean)));

  if (!cleanInstitutions.length) {
    return null;
  }

  if (cleanInstitutions.length <= 3) {
    return cleanInstitutions.join(", ");
  }

  const mainInstitutions = cleanInstitutions.slice(0, 3).join(", ");
  const remainder = cleanInstitutions.length - 3;
  return `${mainInstitutions} +${remainder} outro${remainder > 1 ? "s" : ""}`;
};

const getFieldValue = (source, path) => {
  if (!source || !path) {
    return undefined;
  }
  return path.split(".").reduce((acc, key) => {
    if (acc == null) {
      return undefined;
    }
    return acc[key];
  }, source);
};

const pickStringValue = (source, paths = []) => {
  if (!source) {
    return null;
  }
  for (const path of paths) {
    const value = getFieldValue(source, path);
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
    if (typeof value === "number") {
      return String(value);
    }
  }
  return null;
};

const findFirstArray = (...values) =>
  values.find((item) => Array.isArray(item)) ?? [];

const normalizeText = (value) =>
  typeof value === "string" ? value.toLowerCase().trim() : "";

const normalizeStageValue = (value) => {
  if (value == null) return "";
  return normalizeText(String(value));
};

const matchesStageValue = (value, stageFilter) => {
  const normalizedFilter = normalizeStageValue(stageFilter);
  if (!normalizedFilter) return false;
  const normalizedValue = normalizeStageValue(value);
  if (!normalizedValue) return false;
  return (
    normalizedValue === normalizedFilter ||
    normalizedValue.includes(normalizedFilter) ||
    normalizedFilter.includes(normalizedValue)
  );
};

const matchesStartStageFilter = (deal, stageFilter) => {
  if (!deal) return false;
  const normalizedFilter = normalizeStageValue(stageFilter);
  if (!normalizedFilter) return false;
  const candidates = [
    deal.stage,
    deal.stageName,
    deal.pipelineStage,
    deal.status,
    deal.operationStage,
    deal.funnelStage,
    deal.cardStage,
    deal.cardStageName,
    deal?.stage?.name,
    deal?.stage?.label,
    deal?.stage?.title,
  ];

  return candidates.some((candidate) =>
    matchesStageValue(candidate, normalizedFilter),
  );
};

const groupByStage = (rows = []) => {
  const map = new Map();
  rows.forEach((row) => {
    const stage = row?.stage || "Sem etapa";
    if (!map.has(stage)) {
      map.set(stage, { stage, items: [] });
    }
    map.get(stage).items.push(row);
  });
  return Array.from(map.values());
};

const groupByFunnel = (rows = []) => {
  const map = new Map();
  rows.forEach((row) => {
    const funnel = row?.funnel || "Sem funil";
    if (!map.has(funnel)) {
      map.set(funnel, { funnel, items: [] });
    }
    map.get(funnel).items.push(row);
  });
  return Array.from(map.values());
};

const isPipelineStageName = (value) => {
  const normalized = normalizeText(value);
  if (!normalized) return false;
  return normalized === "pipeline" || normalized.includes("pipeline");
};

const isApprovalStageName = (value) => {
  const normalized = normalizeText(value);
  if (!normalized) return false;
  const negativeKeywords = ["reprov", "recus", "negad", "declin", "reject"];
  if (negativeKeywords.some((keyword) => normalized.includes(keyword))) {
    return false;
  }
  return (
    normalized.includes("aprov") ||
    normalized.includes("approved") ||
    normalized.includes("approval")
  );
};

const includesStartKeyword = (value) => {
  const normalized = normalizeText(value);
  if (!normalized) {
    return false;
  }
  const keywords = [
    "inici",
    "start",
    "abert",
    "open",
    "ativ",
    "liber",
    "entry",
    "opened",
    "started",
  ];

  return keywords.some((keyword) => normalized.includes(keyword));
};

const isDealStarted = (deal) => {
  if (!deal) return false;
  if (deal.started || deal.isStarted || deal.dealStarted || deal.initiated) {
    return true;
  }
  const textSources = [
    deal.stage,
    deal.status,
    deal.stageName,
    deal.operationStage,
    deal.cardStage,
    deal.funnelStage,
    deal.decision,
    deal.type,
  ];

  if (textSources.some((source) => includesStartKeyword(source))) {
    return true;
  }

  return false;
};

const clientNamePaths = [
  "companyName",
  "company.name",
  "company.legalName",
  "company.tradeName",
  "company.displayName",
  "companyLegalName",
  "company.legal_name",
  "legalName",
  "legal_name",
  "customerName",
  "customer.name",
  "customerLegalName",
  "customer.legalName",
  "customer.legal_name",
  "leadName",
  "lead.name",
  "lead.legalName",
  "lead.legal_name",
  "lead.companyName",
  "lead.company.legalName",
  "lead.company.name",
  "lead.company.tradeName",
  "lead.company.displayName",
  "organizationName",
];

const clientEmailPaths = [
  "email",
  "contactEmail",
  "responsibleEmail",
  "company.email",
  "company.contactEmail",
  "company.contact.email",
  "company.contacts.0.email",
  "customer.email",
  "customer.contactEmail",
  "customer.contact.email",
  "lead.email",
  "lead.contactEmail",
  "lead.contact.email",
  "lead.contacts.0.email",
  "contact.email",
];

const clientPhonePaths = [
  "phone",
  "phoneNumber",
  "telephone",
  "mobile",
  "cellphone",
  "responsiblePhone",
  "company.phone",
  "company.phoneNumber",
  "company.telephone",
  "company.mobile",
  "company.contact.phone",
  "company.contacts.0.phone",
  "company.contacts.0.phoneNumber",
  "customer.phone",
  "customer.phoneNumber",
  "customer.contact.phone",
  "lead.phone",
  "lead.phoneNumber",
  "lead.mobile",
  "lead.contact.phone",
  "contact.phone",
  "telefones.0",
  "phones.0",
];

const clientCnpjPaths = [
  "cnpj",
  "document",
  "documentNumber",
  "taxId",
  "company.cnpj",
  "company.document",
  "company.documentNumber",
  "company.cnpj_cpf",
  "company.taxId",
  "company.legalDocument",
  "customer.cnpj",
  "customer.document",
  "lead.cnpj",
  "lead.document",
  "lead.company.cnpj",
  "lead.company.document",
];

const clientPartnerPaths = [
  "company.partners",
  "company.socios",
  "company.socios.socio",
  "company.qsa",
  "company.QSA",
  "partners",
  "socios",
  "socios.socio",
  "qsa",
  "QSA",
  "lead.company.partners",
  "lead.company.socios",
];

const funnelNamePaths = [
  "funnelName",
  "funnel.name",
  "stage.funnel.name",
  "funnel",
  "board.name",
  "pipeline.funnel.name",
];

const funnelIdPaths = [
  "funnelId",
  "funnel.id",
  "stage.funnel.id",
  "funnel_id",
  "boardId",
  "board.id",
  "pipeline.funnel.id",
];

const cardStagePaths = [
  "stageName",
  "stage",
  "pipelineStage",
  "status",
  "operationStage",
  "funnelStage",
  "cardStage",
  "cardStageName",
  "stage.name",
  "stage.label",
  "stage.title",
];

const cardStageIdPaths = ["stageId", "stage.id", "pipelineStageId"];

const cardCreatedAtPaths = ["createdAt", "created_at", "created"];

const cardDatePaths = [
  "createdAt",
  "created_at",
  "created",
  "entryAt",
  "openedAt",
  "startDate",
  "date",
  "updatedAt",
  "decisionAt",
];

const buildCardRow = (card, index, overrides = {}) => {
  const title =
    pickStringValue(card, ["title", "cardTitle", "name"]) ??
    `Card ${index + 1}`;
  const clientEmail = pickStringValue(card, clientEmailPaths);
  const clientPhone = pickPhoneValue(card, clientPhonePaths);
  const clientCnpj = pickStringValue(card, clientCnpjPaths);
  const clientPartners = formatPartnerNames(card);
  const numericValue = pickNumericValue(
    card?.dealValue,
    card?.value,
    card?.amount,
    card?.approvedValue,
  );
  const stage =
    overrides.stageName ?? pickStringValue(card, cardStagePaths) ?? "—";
  const funnel =
    overrides.funnelName ?? pickStringValue(card, funnelNamePaths) ?? "Funil";
  const funnelId =
    overrides.funnelId ?? pickStringValue(card, funnelIdPaths) ?? null;
  const dateField = pickDateValue(card, cardDatePaths);
  const sortTime = toTimestamp(dateField) ?? 0;
  const rawId =
    card?.id ?? card?.cardId ?? card?.identifier ?? card?.card?.id ?? null;
  const fallbackKey = `${normalizeText(title) || "card"}-${
    funnelId || normalizeText(funnel) || "funnel"
  }-${sortTime || index}`;
  const key = rawId != null ? String(rawId) : fallbackKey;

  return {
    id: rawId ?? key,
    key,
    title,
    client: pickStringValue(card, clientNamePaths),
    clientEmail,
    clientPhone,
    clientCnpj,
    clientPartners,
    stage,
    funnel,
    funnelId,
    valueLabel: numericValue != null ? currency(numericValue) : "—",
    dateLabel: dateField ? formatDecisionDate(dateField) : null,
    sortTime,
  };
};

const formatPhoneValue = (value) => {
  if (!value) return null;
  if (typeof value === "string" && value.trim()) return value.trim();
  if (typeof value === "number") return String(value);
  if (Array.isArray(value)) {
    return formatPhoneValue(value[0]);
  }
  if (typeof value === "object") {
    const ddd =
      value.ddd ??
      value.areaCode ??
      value.codigo_area ??
      value.codigoArea ??
      value.codigo;
    const number =
      value.number ??
      value.numero ??
      value.phone ??
      value.telefone ??
      value.mobile ??
      value.celular;
    const parts = [ddd, number].filter(Boolean);
    if (parts.length) {
      return parts.join(" ");
    }
  }
  return null;
};

const pickPhoneValue = (source, paths = []) => {
  if (!source) return null;
  for (const path of paths) {
    const value = getFieldValue(source, path);
    const formatted = formatPhoneValue(value);
    if (formatted) return formatted;
  }
  return null;
};

const normalizePartnerName = (partner) =>
  pickStringValue(partner, [
    "name",
    "nome",
    "nome_socio",
    "fullName",
    "razao_social",
    "legalName",
    "companyName",
    "nome_empresa",
  ]);

const formatPartnerNames = (source) => {
  if (!source) return null;
  const candidates = clientPartnerPaths.map((path) =>
    getFieldValue(source, path),
  );
  const raw =
    findFirstArray(...candidates) ??
    candidates.find((value) => typeof value === "string" && value.trim()) ??
    null;
  if (!raw) return null;
  if (typeof raw === "string") {
    return raw.trim();
  }
  if (!Array.isArray(raw)) {
    return normalizePartnerName(raw);
  }
  const names = raw
    .map((partner) => normalizePartnerName(partner))
    .filter(Boolean);
  return names.length ? names.join(", ") : null;
};

const getDecisionApprovedValue = (decision) =>
  pickNumericValue(
    decision?.approvedValue,
    decision?.approvedAmount,
    decision?.amount,
    decision?.value,
    decision?.drawnValue,
  );

const matchesDecisionStatus = (decision) => {
  if (!decision) return null;
  const candidates = [
    decision.status,
    decision.decision,
    decision.operationType,
    decision.operation,
    decision.stage,
    decision.cardType,
  ];

  for (const candidate of candidates) {
    const normalized =
      typeof candidate === "string"
        ? candidate.toLowerCase()
        : typeof candidate === "number"
          ? String(candidate).toLowerCase()
          : "";
    if (!normalized) continue;
    if (normalized.includes("aprov") || normalized.includes("approved")) {
      return true;
    }
    if (
      normalized.includes("reprov") ||
      normalized.includes("recus") ||
      normalized.includes("negad") ||
      normalized.includes("reject") ||
      normalized.includes("declin") ||
      normalized.includes("deny")
    ) {
      return false;
    }
  }

  return null;
};

const isDecisionApproved = (decision) => {
  if (!decision) return false;
  if (typeof decision.approved === "boolean") {
    return decision.approved;
  }

  const statusMatch = matchesDecisionStatus(decision);
  if (statusMatch != null) {
    return statusMatch;
  }

  const numericValue = getDecisionApprovedValue(decision);
  if (numericValue != null) {
    return toNumber(numericValue) > 0;
  }

  return false;
};

const fetchOperationsDashboard = ({
  range,
  month,
  startDate,
  endDate,
  funnelIds = [],
}) => {
  const params = {};
  if (range) {
    params.range = range;
  }
  if (month) {
    params.month = month;
  }
  if (startDate) {
    params.startDate = startDate;
  }
  if (endDate) {
    params.endDate = endDate;
  }
  if (funnelIds?.length) {
    params.funnelIds = funnelIds;
  }
  return axiosInstance
    .get(DASHBOARD.GET_OPERATIONS, {
      params,
    })
    .then((response) => response.data.data);
};

const fetchOperationsDashboardAnalysis = ({
  range,
  month,
  startDate,
  endDate,
  funnelIds = [],
}) => {
  const params = {};
  if (range) {
    params.range = range;
  }
  if (month) {
    params.month = month;
  }
  if (startDate) {
    params.startDate = startDate;
  }
  if (endDate) {
    params.endDate = endDate;
  }
  if (funnelIds?.length) {
    params.funnelIds = funnelIds;
  }
  return axiosInstance
    .get(DASHBOARD.GET_OPERATIONS_ANALYSIS, {
      params,
    })
    .then((response) => response.data.data);
};

const fetchPipelineCards = async (funnels = []) => {
  if (!funnels.length) return [];
  const results = await Promise.allSettled(
    funnels.map((funnel) =>
      axiosInstance.get(`${CRM.FUNNEL_DETAILS}/${funnel.id}/details`),
    ),
  );

  const cards = [];
  results.forEach((result, index) => {
    if (result.status !== "fulfilled") return;
    const funnelData = result.value?.data?.data;
    const funnelName = funnelData?.name || funnels[index]?.name || "Funil";
    const funnelId = funnelData?.id ?? funnels[index]?.id;
    const stages = Array.isArray(funnelData?.stages) ? funnelData.stages : [];

    stages.forEach((stage) => {
      const stageCards = Array.isArray(stage?.cards) ? stage.cards : [];
      stageCards.forEach((card) => {
        cards.push({
          ...card,
          funnelId,
          funnelName,
          stageId: card?.stageId ?? stage?.id ?? card?.stage?.id ?? null,
          stageName: stage?.name || "Etapa",
        });
      });
    });
  });

  return cards;
};

const fetchFunnels = () =>
  axiosInstance
    .get(CRM.FUNNELS)
    .then((response) => response.data.data ?? [])
    .then((list) =>
      Array.isArray(list) ? list.filter((f) => f?.isMetrified === true) : [],
    );

export default function DashboardCRMNew() {
  const brand = "#A60B3E";
  const chartColor = "#1F2933";
  const chartPalette = Array(5).fill(chartColor);
  const { userPermission } = useApplication();
  const isPowerUser = String(userPermission || "").includes("POWER_USER");
  const regionPalette = [
    "#06B6D4",
    "#F59E0B",
    "#10B981",
    "#6366F1",
    "#EC4899",
    "#F97316",
  ];
  const approvalPalette = [brand, "#F97316", "#6366F1", "#22D3EE", "#14B8A6"];
  const [dateRange, setDateRange] = useState(buildDefaultDateRange);
  const [committedRange, setCommittedRange] = useState(buildDefaultDateRange);
  const [rangeStart, rangeEnd] = dateRange;
  const [committedStart, committedEnd] = committedRange;
  const rangeLabel = useMemo(() => {
    if (committedStart && committedEnd) {
      return `${formatDecisionDate(committedStart)} a ${formatDecisionDate(
        committedEnd,
      )}`;
    }
    return "Selecione o período";
  }, [committedStart, committedEnd]);
  const periodParams = useMemo(() => {
    if (!committedStart || !committedEnd) return {};
    return {
      startDate: formatDateKey(committedStart),
      endDate: formatDateKey(committedEnd),
    };
  }, [committedStart, committedEnd]);
  const rangeBounds = useMemo(() => {
    if (!committedStart || !committedEnd) {
      return null;
    }
    const start = new Date(committedStart);
    start.setHours(0, 0, 0, 0);
    const end = new Date(committedEnd);
    end.setHours(23, 59, 59, 999);
    return {
      startTime: start.getTime(),
      endTime: end.getTime(),
    };
  }, [committedStart, committedEnd]);
  const handleDateRangeChange = (range) => {
    setDateRange(range);
    const [start, end] = range ?? [];
    if (start && end) {
      setCommittedRange([start, end]);
    }
  };

  const handleMonthRangePick = (date) => {
    if (!date) {
      return;
    }
    const year = date.getUTCFullYear();
    const month = date.getUTCMonth();
    const start = new Date(year, month, 1);
    const monthEnd = new Date(year, month + 1, 0);
    const today = new Date();
    const todayLocal = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
    );
    const end = monthEnd > todayLocal ? todayLocal : monthEnd;
    setDateRange([start, end]);
    setCommittedRange([start, end]);
  };
  const [selectedFunnelIds, setSelectedFunnelIds] = useState([]);
  const [funnelSelectionValue, setFunnelSelectionValue] = useState("");

  const { data, isLoading, isError, error, refetch } = useQuery(
    [
      "operationsDashboard",
      periodParams.startDate,
      periodParams.endDate,
      selectedFunnelIds.join(","),
    ],
    () =>
      fetchOperationsDashboard({
        ...periodParams,
        funnelIds: selectedFunnelIds,
      }),
    {
      enabled:
        selectedFunnelIds.length > 0 &&
        Boolean(periodParams.startDate && periodParams.endDate),
    },
  );

  const {
    data: funnels = [],
    isLoading: funnelsLoading,
    isError: funnelsError,
    error: funnelsErrorObj,
    refetch: refetchFunnels,
  } = useQuery("crmFunnels", fetchFunnels);

  const {
    data: pipelineCards = [],
    isLoading: pipelineLoading,
    isError: pipelineError,
    error: pipelineErrorObj,
  } = useQuery(
    ["pipelineCards", funnels.map((f) => f.id).join(",")],
    () => fetchPipelineCards(funnels),
    { enabled: funnels.length > 0 },
  );

  useEffect(() => {
    if (!selectedFunnelIds.length && funnels.length) {
      setSelectedFunnelIds([funnels[0].id]);
    }
  }, [funnels, selectedFunnelIds]);

  const selectedFunnels = funnels.filter((funnel) =>
    selectedFunnelIds.includes(funnel.id),
  );
  const hasFunnelSelection = selectedFunnelIds.length > 0;
  const noFunnelConfigured = !funnelsLoading && !funnels.length;
  const waitingForFunnelSelection = !hasFunnelSelection && !noFunnelConfigured;
  const selectedFunnelHeading =
    selectedFunnels.length === 1
      ? selectedFunnels[0]?.name
      : selectedFunnels.length > 1
        ? `${selectedFunnels.length} quadros selecionados`
        : "Quadro corporativo";
  const selectedFunnelTimelineLabel =
    selectedFunnels.length === 1
      ? selectedFunnels[0]?.name || "Quadro"
      : selectedFunnels.length > 1
        ? `${selectedFunnels.length} quadros`
        : "Quadro";

  const funnelStartStageMap = useMemo(() => {
    const byId = new Map();
    const byName = new Map();
    selectedFunnels.forEach((funnel) => {
      if (!funnel) return;
      const startStageId =
        funnel.startStageId ??
        funnel.startStage ??
        funnel.startStageName ??
        null;
      if (!startStageId) return;
      const stages = Array.isArray(funnel.stages) ? funnel.stages : [];
      const match = stages.find(
        (stage) => (stage?.id || stage?.name) === startStageId,
      );
      const stageName =
        match?.name || (typeof startStageId === "string" ? startStageId : "");
      if (!stageName) return;
      if (funnel.id != null) {
        byId.set(String(funnel.id), stageName);
      }
      if (funnel.name) {
        byName.set(normalizeText(funnel.name), stageName);
      }
    });
    return { byId, byName };
  }, [selectedFunnels]);

  const funnelStartStageIdMap = useMemo(() => {
    const byId = new Map();
    const byName = new Map();
    selectedFunnels.forEach((funnel) => {
      if (!funnel) return;
      const startStageId =
        funnel.startStageId ??
        funnel.startStage?.id ??
        funnel.startStage ??
        null;
      if (!startStageId) return;
      if (funnel.id != null) {
        byId.set(String(funnel.id), String(startStageId));
      }
      if (funnel.name) {
        byName.set(normalizeText(funnel.name), String(startStageId));
      }
    });
    return { byId, byName };
  }, [selectedFunnels]);

  const funnelStageOrderMap = useMemo(() => {
    const byId = new Map();
    const byName = new Map();
    selectedFunnels.forEach((funnel) => {
      if (!funnel) return;
      const stages = Array.isArray(funnel.stages) ? funnel.stages : [];
      const stageById = new Map();
      const stageByName = new Map();
      stages.forEach((stage, index) => {
        if (!stage) return;
        const order = Number.isFinite(Number(stage.order))
          ? Number(stage.order)
          : index;
        if (stage.id != null) {
          stageById.set(String(stage.id), order);
        }
        if (stage.name) {
          stageByName.set(normalizeText(stage.name), order);
        }
      });
      if (funnel.id != null) {
        byId.set(String(funnel.id), { byId: stageById, byName: stageByName });
      }
      if (funnel.name) {
        byName.set(normalizeText(funnel.name), {
          byId: stageById,
          byName: stageByName,
        });
      }
    });
    return { byId, byName };
  }, [selectedFunnels]);

  const toggleFunnelSelection = (funnelId) => {
    setSelectedFunnelIds((prev) => {
      if (prev.includes(funnelId)) {
        return prev.filter((id) => id !== funnelId);
      }
      return [...prev, funnelId];
    });
  };

  const handleFunnelSelectChange = (event) => {
    const value = event.target.value;
    if (!value) {
      setFunnelSelectionValue("");
      return;
    }
    if (!selectedFunnelIds.includes(value)) {
      setSelectedFunnelIds((prev) => [...prev, value]);
    }
    setFunnelSelectionValue("");
  };

  const summary = data?.summary ?? {};
  const timeline = data?.timeline ?? [];
  const rawStageDurations = data?.stageDurations ?? [];
  const approvalsByBank = data?.approvalsByBank ?? [];
  const declineReasons = data?.declineReasons ?? [];
  const taskCompletion = data?.taskCompletion ?? [];
  const regionDistribution = data?.regionDistribution ?? [];
  const regionLeads = data?.regionLeads ?? [];
  const segmentDistribution = data?.segmentDistribution ?? [];
  const revenue = summary.revenue ?? 0;
  const leadsTotal = Number(summary.leadsTotal ?? 0);
  const leadsConverted = Number(summary.leadsConverted ?? 0);
  const leadConversionRate =
    leadsTotal > 0 ? (leadsConverted / leadsTotal) * 100 : null;

  const stageDurations = useMemo(() => {
    if (selectedFunnelIds.length <= 1) {
      return rawStageDurations;
    }

    const stagesMap = new Map();
    const order = [];

    rawStageDurations.forEach((stage, index) => {
      const stageName =
        typeof stage?.stageName === "string"
          ? stage.stageName.trim()
          : stage?.stageName;
      const key =
        normalizeText(stageName) ||
        normalizeText(stage?.stageId) ||
        `stage-${index}`;

      if (!stagesMap.has(key)) {
        stagesMap.set(key, {
          stageKey: key,
          stageId: stage?.stageId ?? key,
          stageName: stageName || stage?.stageName || "Etapa sem nome",
          entries: 0,
          weightedDays: 0,
          weightedHours: 0,
          weight: 0,
        });
        order.push(key);
      }

      const item = stagesMap.get(key);
      const entries = Number(stage?.entries ?? 0);
      const averageDays = Number(stage?.averageDays ?? 0);
      const averageHours = Number(stage?.averageHours ?? 0);
      const weight = entries > 0 ? entries : 1;

      item.entries += entries;
      item.weight += weight;
      item.weightedDays += averageDays * weight;
      item.weightedHours += averageHours * weight;
    });

    return order.map((key) => {
      const item = stagesMap.get(key);
      const denominator = item.weight || 1;
      return {
        stageKey: item.stageKey,
        stageId: item.stageId,
        stageName: item.stageName,
        entries: item.entries,
        averageDays: item.weightedDays / denominator,
        averageHours: item.weightedHours / denominator,
      };
    });
  }, [rawStageDurations, selectedFunnelIds]);

  const approvalStages = useMemo(
    () =>
      stageDurations.filter((stage) => isApprovalStageName(stage.stageName)),
    [stageDurations],
  );

  const approvalStageEntries = useMemo(
    () =>
      approvalStages.reduce(
        (sum, stage) => sum + Number(stage.entries ?? 0),
        0,
      ),
    [approvalStages],
  );

  const stageDurationsWithEffectiveness = useMemo(() => {
    const hasApprovalStage = approvalStages.length > 0;
    return stageDurations.map((stage) => {
      const entries = Number(stage.entries ?? 0);
      const effectivenessRate =
        hasApprovalStage && entries > 0
          ? Math.min(100, (approvalStageEntries / entries) * 100)
          : null;
      return {
        ...stage,
        effectivenessRate,
      };
    });
  }, [stageDurations, approvalStageEntries, approvalStages.length]);

  const getEffectivenessTone = (value) => {
    if (value == null) {
      return {
        label: "Sem dados",
        text: "text-gray-400",
        bar: "bg-gray-200",
        glow: "bg-gray-100",
      };
    }
    if (value >= 60) {
      return {
        label: "Alta",
        text: "text-emerald-700",
        bar: "bg-emerald-500",
        glow: "bg-emerald-100",
      };
    }
    if (value >= 30) {
      return {
        label: "Média",
        text: "text-amber-700",
        bar: "bg-amber-500",
        glow: "bg-amber-100",
      };
    }
    return {
      label: "Baixa",
      text: "text-rose-700",
      bar: "bg-rose-500",
      glow: "bg-rose-100",
    };
  };

  const [isDealsModalOpen, setIsDealsModalOpen] = useState(false);
  const [isApprovalsModalOpen, setIsApprovalsModalOpen] = useState(false);
  const [isAmpliaDeclinesModalOpen, setIsAmpliaDeclinesModalOpen] =
    useState(false);
  const [isPipelineModalOpen, setIsPipelineModalOpen] = useState(false);
  const [isRegionModalOpen, setIsRegionModalOpen] = useState(false);
  const [selectedRegionLabel, setSelectedRegionLabel] = useState(null);
  const [isAiAnalysisModalOpen, setIsAiAnalysisModalOpen] = useState(false);
  const [isGeneratingAiAnalysis, setIsGeneratingAiAnalysis] = useState(false);
  const [aiAnalysisSnapshot, setAiAnalysisSnapshot] = useState(null);
  const [aiAnalysisError, setAiAnalysisError] = useState(null);

  const resolveStartStageForDeal = useCallback(
    (deal) => {
      if (!deal) return null;
      const dealFunnelId = pickStringValue(deal, funnelIdPaths);
      if (dealFunnelId && funnelStartStageMap.byId.has(dealFunnelId)) {
        return funnelStartStageMap.byId.get(dealFunnelId);
      }
      const dealFunnelName = pickStringValue(deal, funnelNamePaths);
      if (dealFunnelName) {
        const key = normalizeText(dealFunnelName);
        if (funnelStartStageMap.byName.has(key)) {
          return funnelStartStageMap.byName.get(key);
        }
      }
      return null;
    },
    [funnelStartStageMap],
  );

  const isDealStartedByConfig = useCallback(
    (deal) => {
      const startStage = resolveStartStageForDeal(deal);
      if (startStage) {
        return matchesStartStageFilter(deal, startStage);
      }
      return isDealStarted(deal);
    },
    [resolveStartStageForDeal],
  );

  const normalizedDealRows = useMemo(() => {
    const rawSourceArray = findFirstArray(
      data?.dealsStartedList,
      data?.initiatedDeals,
      data?.startedDeals,
      data?.operations,
      data?.deals,
      data?.businesses,
      data?.cards,
      data?.cardsStarted,
    );
    const sourceArray = rawSourceArray.filter((deal) =>
      isDealStartedByConfig(deal),
    );

    if (!sourceArray.length) {
      return [];
    }

    return sourceArray.map((deal, index) => {
      const id =
        deal?.id ??
        deal?.cardId ??
        deal?.identifier ??
        `${deal?.title ?? deal?.label ?? "deal"}-${index}`;
      const cardTitle =
        pickStringValue(deal, [
          "cardTitle",
          "title",
          "name",
          "dealName",
          "operationName",
          "caseTitle",
        ]) ?? null;
      const clientEmail = pickStringValue(deal, clientEmailPaths);
      const clientPhone = pickPhoneValue(deal, clientPhonePaths);
      const clientCnpj = pickStringValue(deal, clientCnpjPaths);
      const clientPartners = formatPartnerNames(deal);
      const numericValue = pickNumericValue(
        deal?.amount,
        deal?.value,
        deal?.dealValue,
        deal?.approvedValue,
        deal?.approvedAmount,
        deal?.drawnValue,
      );
      const dateField = pickStringValue(deal, [
        "createdAt",
        "startedAt",
        "decisionAt",
        "entryAt",
        "date",
      ]);

      return {
        id,
        title: cardTitle ?? `Negócio ${index + 1}`,
        cardTitle,
        client: pickStringValue(deal, clientNamePaths),
        clientEmail,
        clientPhone,
        clientCnpj,
        clientPartners,
        funnelId: pickStringValue(deal, funnelIdPaths),
        funnel:
          pickStringValue(deal, [
            "funnelName",
            "funnel.name",
            "stage.funnel.name",
            "funnel",
          ]) ?? null,
        stage:
          pickStringValue(deal, [
            "stage",
            "pipelineStage",
            "status",
            "operationStage",
            "funnelStage",
          ]) ?? "—",
        valueLabel: numericValue != null ? currency(numericValue) : "—",
        dateLabel: dateField ? formatDecisionDate(dateField) : null,
      };
    });
  }, [data, isDealStartedByConfig]);

  const pipelineStageCardRows = useMemo(() => {
    if (!pipelineCards.length) {
      return [];
    }

    return pipelineCards
      .filter((card) => {
        if (
          !isPipelineStageName(
            card?.stageName ??
              card?.stage ??
              card?.stage?.name ??
              card?.status ??
              "",
          )
        ) {
          return false;
        }
        if (rangeBounds) {
          const dateField = pickDateValue(card, cardDatePaths);
          if (
            !isWithinRange(
              dateField,
              rangeBounds.startTime,
              rangeBounds.endTime,
            )
          ) {
            return false;
          }
        }
        return true;
      })
      .map((card, index) =>
        buildCardRow(card, index, {
          stageName: card?.stageName || "Pipeline",
          funnelName: card?.funnelName,
          funnelId: card?.funnelId,
        }),
      );
  }, [pipelineCards, rangeBounds]);

  const createdPeriodCardRows = useMemo(() => {
    if (!pipelineCards.length) {
      return [];
    }
    if (!rangeBounds) {
      return [];
    }
    return pipelineCards
      .map((card, index) => {
        const dateField = pickDateValue(card, cardDatePaths);
        if (
          !isWithinRange(dateField, rangeBounds.startTime, rangeBounds.endTime)
        ) {
          return null;
        }
        return buildCardRow(card, index, {
          stageName: card?.stageName,
          funnelName: card?.funnelName,
          funnelId: card?.funnelId,
        });
      })
      .filter(Boolean);
  }, [pipelineCards, rangeBounds]);

  const pipelineCardRows = useMemo(() => {
    const map = new Map();
    const addRow = (row) => {
      if (!row) return;
      const key = row.key ?? row.id;
      if (key == null) return;
      if (!map.has(key)) {
        map.set(key, row);
      }
    };
    pipelineStageCardRows.forEach(addRow);
    createdPeriodCardRows.forEach(addRow);
    return Array.from(map.values()).sort(
      (a, b) => (b.sortTime || 0) - (a.sortTime || 0),
    );
  }, [pipelineStageCardRows, createdPeriodCardRows]);

  const startedCardsRows = useMemo(() => {
    if (!pipelineCards.length || !selectedFunnelIds.length) {
      return [];
    }
    const selectedIdSet = new Set(selectedFunnelIds.map(String));
    const selectedNameSet = new Set(
      selectedFunnels.map((funnel) => normalizeText(funnel.name)),
    );
    const hasRange = Boolean(rangeBounds?.startTime && rangeBounds?.endTime);

    const resolveStageOrder = (
      cardFunnelId,
      cardFunnelName,
      stageId,
      stageName,
    ) => {
      if (!cardFunnelId && !cardFunnelName) return null;
      const funnelKey = cardFunnelId
        ? String(cardFunnelId)
        : normalizeText(cardFunnelName);
      const mapEntry = cardFunnelId
        ? funnelStageOrderMap.byId.get(funnelKey)
        : funnelStageOrderMap.byName.get(funnelKey);
      if (!mapEntry) return null;
      if (stageId && mapEntry.byId.has(String(stageId))) {
        return mapEntry.byId.get(String(stageId));
      }
      const normalizedStageName = normalizeText(stageName);
      if (normalizedStageName && mapEntry.byName.has(normalizedStageName)) {
        return mapEntry.byName.get(normalizedStageName);
      }
      return null;
    };

    return pipelineCards
      .filter((card) => {
        const funnelId = pickStringValue(card, funnelIdPaths);
        const funnelName = pickStringValue(card, funnelNamePaths);
        if (funnelId && selectedIdSet.has(String(funnelId))) {
          return true;
        }
        if (funnelName && selectedNameSet.has(normalizeText(funnelName))) {
          return true;
        }
        return false;
      })
      .map((card, index) => {
        const funnelId = pickStringValue(card, funnelIdPaths);
        const funnelName = pickStringValue(card, funnelNamePaths);
        const startStageId =
          (funnelId && funnelStartStageIdMap.byId.get(String(funnelId))) ||
          (funnelName &&
            funnelStartStageIdMap.byName.get(normalizeText(funnelName))) ||
          null;
        const startStageName =
          (funnelId && funnelStartStageMap.byId.get(String(funnelId))) ||
          (funnelName &&
            funnelStartStageMap.byName.get(normalizeText(funnelName))) ||
          null;
        const cardStageId = pickStringValue(card, cardStageIdPaths);
        const cardStageName =
          pickStringValue(card, cardStagePaths) || card?.stageName || null;
        const isStartStage =
          (startStageId &&
            cardStageId &&
            String(startStageId) === String(cardStageId)) ||
          (startStageName && matchesStageValue(cardStageName, startStageName));

        const startOrder = resolveStageOrder(
          funnelId,
          funnelName,
          startStageId,
          startStageName,
        );
        const currentOrder = resolveStageOrder(
          funnelId,
          funnelName,
          cardStageId,
          cardStageName,
        );
        const isAfterStart =
          currentOrder != null && startOrder != null
            ? currentOrder > startOrder
            : !isStartStage;

        const createdAt = pickDateValue(card, cardCreatedAtPaths);
        const createdWithinRange = hasRange
          ? isWithinRange(createdAt, rangeBounds.startTime, rangeBounds.endTime)
          : true;

        if (!isStartStage && !(isAfterStart && createdWithinRange)) {
          return null;
        }

        return buildCardRow(card, index, {
          stageName: card?.stageName,
          funnelName: card?.funnelName,
          funnelId: card?.funnelId,
        });
      })
      .filter(Boolean);
  }, [
    pipelineCards,
    selectedFunnelIds,
    selectedFunnels,
    rangeBounds,
    funnelStageOrderMap,
    funnelStartStageMap,
    funnelStartStageIdMap,
  ]);

  const filteredPipelineCardRows = useMemo(() => {
    if (!pipelineCardRows.length || !selectedFunnelIds.length) {
      return [];
    }
    const selectedIdSet = new Set(selectedFunnelIds);
    const selectedNameSet = new Set(
      selectedFunnels.map((funnel) => normalizeText(funnel.name)),
    );
    return pipelineCardRows.filter((card) => {
      if (card?.funnelId && selectedIdSet.has(card.funnelId)) {
        return true;
      }
      if (card?.funnel && selectedNameSet.has(normalizeText(card.funnel))) {
        return true;
      }
      return false;
    });
  }, [pipelineCardRows, selectedFunnelIds, selectedFunnels]);

  const pipelineFunnelGroups = useMemo(
    () => groupByFunnel(filteredPipelineCardRows),
    [filteredPipelineCardRows],
  );

  const regionLeadMap = useMemo(
    () =>
      new Map(
        (regionLeads || []).map((entry) => [entry.label, entry.items ?? []]),
      ),
    [regionLeads],
  );

  const selectedRegionLeads = useMemo(
    () =>
      selectedRegionLabel ? (regionLeadMap.get(selectedRegionLabel) ?? []) : [],
    [regionLeadMap, selectedRegionLabel],
  );

  const selectedRegionRows = useMemo(
    () =>
      selectedRegionLeads.map((lead, index) => {
        const numericValue = pickNumericValue(
          lead?.value,
          lead?.dealValue,
          lead?.amount,
        );
        return {
          id: lead?.id ?? `${selectedRegionLabel || "estado"}-${index}`,
          title:
            lead?.name || lead?.companyName || lead?.legalName || "Cliente",
          document: lead?.document || null,
          city: lead?.city || lead?.addressCity || null,
          funnel: lead?.funnel || null,
          stage: lead?.stage || null,
          valueLabel: formatCurrencyOrPlaceholder(numericValue),
          dateLabel: lead?.createdAt
            ? formatDecisionDate(lead.createdAt)
            : null,
        };
      }),
    [selectedRegionLeads, selectedRegionLabel],
  );

  const positiveTimelineEntries = useMemo(
    () => timeline.filter((entry) => toNumber(entry.count) > 0),
    [timeline],
  );

  const timelineAggregatedDeals = useMemo(
    () =>
      positiveTimelineEntries.map((entry, index) => ({
        id: `${entry.label ?? entry.date ?? index}-${entry.count ?? 0}`,
        title: entry.label ?? entry.date ?? `Dia ${index + 1}`,
        client: null,
        stage: entry.stageName ?? entry.stage ?? "—",
        valueLabel: `${entry.count ?? 0} negócios iniciados`,
        count: toNumber(entry.count) ?? 0,
        dateLabel: entry.label ?? entry.date ?? null,
      })),
    [positiveTimelineEntries],
  );

  const timelineDayDetails = useMemo(
    () =>
      positiveTimelineEntries
        .map((entry, index) => {
          const detailsSource = findFirstArray(
            entry.deals,
            entry.cards,
            entry.leads,
            entry.businesses,
            entry.operations,
            entry.items,
            entry.records,
          );

          const filteredDetails = detailsSource?.filter((deal) =>
            isDealStartedByConfig(deal),
          );

          if (!filteredDetails?.length) {
            return null;
          }

          const deals = filteredDetails.map((deal, detailIndex) => {
            const numericValue = pickNumericValue(
              deal?.amount,
              deal?.value,
              deal?.dealValue,
              deal?.approvedValue,
              deal?.approvedAmount,
              deal?.drawnValue,
            );
            const title =
              pickStringValue(deal, [
                "cardTitle",
                "title",
                "name",
                "dealName",
                "operationName",
                "caseTitle",
              ]) ?? `Negócio ${detailIndex + 1}`;
            return {
              id:
                deal?.id ??
                deal?.cardId ??
                `${entry.label ?? entry.date ?? index}-${detailIndex}`,
              title,
              cardTitle: title,
              client: pickStringValue(deal, clientNamePaths),
              clientEmail: pickStringValue(deal, clientEmailPaths),
              clientPhone: pickPhoneValue(deal, clientPhonePaths),
              clientCnpj: pickStringValue(deal, clientCnpjPaths),
              clientPartners: formatPartnerNames(deal),
              funnel:
                pickStringValue(deal, [
                  "funnelName",
                  "funnel.name",
                  "stage.funnel.name",
                  "funnel",
                ]) ?? null,
              stage:
                pickStringValue(deal, [
                  "stage",
                  "stageName",
                  "status",
                  "operationStage",
                ]) ?? "—",
              valueLabel: numericValue != null ? currency(numericValue) : "—",
            };
          });

          return {
            id: `${entry.label ?? entry.date ?? index}-${entry.count ?? 0}`,
            title: entry.label ?? entry.date ?? `Dia ${index + 1}`,
            dateLabel: entry.label ?? entry.date ?? null,
            stage: entry.stageName ?? entry.stage ?? "—",
            count: toNumber(entry.count) ?? deals.length,
            deals,
          };
        })
        .filter(Boolean),
    [positiveTimelineEntries, isDealStartedByConfig],
  );

  const timelineDealRows = useMemo(
    () =>
      timelineDayDetails.flatMap((day) =>
        (day.deals ?? []).map((deal) => ({
          ...deal,
          dateLabel: day.dateLabel ?? day.title,
        })),
      ),
    [timelineDayDetails],
  );

  const aggregatedStartStageName = useMemo(() => {
    const names = new Set();
    funnelStartStageMap.byId.forEach((value) => {
      if (value) names.add(value);
    });
    funnelStartStageMap.byName.forEach((value) => {
      if (value) names.add(value);
    });
    if (names.size === 1) {
      return Array.from(names)[0];
    }
    return null;
  }, [funnelStartStageMap]);

  const filteredTimelineAggregatedDeals = useMemo(() => {
    if (!aggregatedStartStageName) {
      return timelineAggregatedDeals;
    }
    return timelineAggregatedDeals.filter((entry) =>
      matchesStageValue(entry.stage, aggregatedStartStageName),
    );
  }, [timelineAggregatedDeals, aggregatedStartStageName]);

  const tradesFallbackDetails = timelineDayDetails;
  const dealsStageGroups = useMemo(() => {
    if (startedCardsRows.length) {
      return { source: "computed", groups: groupByStage(startedCardsRows) };
    }
    if (normalizedDealRows.length) {
      return { source: "direct", groups: groupByStage(normalizedDealRows) };
    }
    if (timelineDealRows.length) {
      return { source: "timeline", groups: groupByStage(timelineDealRows) };
    }
    if (tradesFallbackDetails.length) {
      const flattened = tradesFallbackDetails.flatMap((day) =>
        (day.deals ?? []).map((deal, index) => ({
          ...deal,
          id: deal?.id ?? `${day.id}-${index}`,
          dateLabel: day.dateLabel ?? day.title,
        })),
      );
      return { source: "details", groups: groupByStage(flattened) };
    }
    if (filteredTimelineAggregatedDeals.length) {
      return {
        source: "aggregated",
        groups: groupByStage(filteredTimelineAggregatedDeals),
      };
    }
    return { source: null, groups: [] };
  }, [
    startedCardsRows,
    normalizedDealRows,
    timelineDealRows,
    tradesFallbackDetails,
    filteredTimelineAggregatedDeals,
  ]);
  const hasDealsData =
    startedCardsRows.length > 0 ||
    normalizedDealRows.length > 0 ||
    timelineDealRows.length > 0 ||
    tradesFallbackDetails.length > 0 ||
    filteredTimelineAggregatedDeals.length > 0;
  const hasPipelineCards = filteredPipelineCardRows.length > 0;
  const dealsStartedMetricCount = useMemo(() => {
    const hasStartStageConfig =
      funnelStartStageMap.byId.size > 0 || funnelStartStageMap.byName.size > 0;
    if (!hasStartStageConfig) {
      return summary.dealsStarted ?? 0;
    }

    if (startedCardsRows.length) {
      return startedCardsRows.length;
    }

    if (normalizedDealRows.length) {
      return normalizedDealRows.length;
    }

    if (timelineDealRows.length) {
      return timelineDealRows.length;
    }

    if (filteredTimelineAggregatedDeals.length) {
      return filteredTimelineAggregatedDeals.reduce(
        (sum, entry) => sum + (toNumber(entry.count) ?? 0),
        0,
      );
    }

    return 0;
  }, [
    funnelStartStageMap,
    summary.dealsStarted,
    startedCardsRows,
    normalizedDealRows,
    timelineDealRows,
    filteredTimelineAggregatedDeals,
  ]);
  const handleDealsCardClick = () => {
    if (!hasDealsData) {
      return;
    }
    setIsDealsModalOpen(true);
  };

  const handleApprovalsCardClick = () => {
    if (!hasApprovalsData) {
      return;
    }
    setIsApprovalsModalOpen(true);
  };

  const handleAmpliaDeclinesCardClick = () => {
    if (!amplaDeclineBanks.length) {
      return;
    }
    setIsAmpliaDeclinesModalOpen(true);
  };

  const handlePipelineCardClick = () => {
    if (!hasPipelineCards) {
      return;
    }
    setIsPipelineModalOpen(true);
  };

  const canExportDashboard =
    isPowerUser && hasFunnelSelection && !isLoading && data;

  const handleExportDashboard = () => {
    if (!data || !isPowerUser) {
      return;
    }

    const workbook = XLSX.utils.book_new();
    const safeSheetName = (name) => name.slice(0, 31);
    const headerStyle = {
      fill: { patternType: "solid", fgColor: { rgb: "E5E7EB" } },
      font: { bold: true, color: { rgb: "111827" } },
    };
    const addSheet = (name, rows) => {
      const safeRows = rows?.length ? rows : [{ Info: "Sem dados" }];
      const headers = [];
      const seenHeaders = new Set();
      safeRows.forEach((row) => {
        Object.keys(row || {}).forEach((key) => {
          if (seenHeaders.has(key)) return;
          seenHeaders.add(key);
          headers.push(key);
        });
      });
      const data = [
        headers,
        ...safeRows.map((row) =>
          headers.map((key) => {
            const value = row?.[key];
            return value == null ? "" : value;
          }),
        ),
      ];
      const sheet = XLSX.utils.aoa_to_sheet(data);
      headers.forEach((_, index) => {
        const cellAddress = XLSX.utils.encode_cell({ r: 0, c: index });
        if (!sheet[cellAddress]) return;
        sheet[cellAddress].s = headerStyle;
      });
      XLSX.utils.book_append_sheet(workbook, sheet, safeSheetName(name));
    };

    const selectedFunnelsLabel = selectedFunnels.length
      ? selectedFunnels.map((funnel) => funnel.name).join(", ")
      : "Todos";

    addSheet("Parametros", [
      {
        Periodo: rangeLabel,
        Tipo: "range",
        Valor: rangeLabel,
        DataInicial: periodParams.startDate,
        DataFinal: periodParams.endDate,
        Funis: selectedFunnelsLabel,
      },
    ]);

    addSheet("Resumo", [
      { Indicador: "Negocios iniciados", Valor: dealsStartedMetricCount },
      { Indicador: "Valor aprovado", Valor: kpiApprovedValue ?? 0 },
      { Indicador: "Valor reprovado", Valor: summary.rejectedValue ?? 0 },
      { Indicador: "Declinacoes Amplia", Valor: amplaDeclineDecisionCount },
      { Indicador: "Instituicoes avaliadas", Valor: kpiInstitutionsCount ?? 0 },
    ]);

    addSheet("Conversao Leads", [
      {
        LeadsTotal: leadsTotal,
        LeadsConvertidos: leadsConverted,
        ConversaoPercentual:
          leadConversionRate != null
            ? Number(leadConversionRate.toFixed(2))
            : null,
      },
    ]);

    addSheet(
      "Efetividade Etapas",
      stageDurationsWithEffectiveness.map((stage) => ({
        Etapa: stage.stageName,
        Entradas: Number(stage.entries ?? 0),
        EfetividadePercentual:
          stage.effectivenessRate != null
            ? Number(stage.effectivenessRate.toFixed(2))
            : null,
      })),
    );

    addSheet(
      "Etapas Duracao",
      stageDurations.map((stage) => ({
        Etapa: stage.stageName,
        Entradas: Number(stage.entries ?? 0),
        MediaDias: Number(stage.averageDays ?? 0),
        MediaHoras: Number(stage.averageHours ?? 0),
      })),
    );

    addSheet(
      "Timeline",
      timeline.map((entry) => ({
        Data: entry.label ?? entry.date ?? "",
        Negocios: Number(entry.count ?? 0),
        Etapa: entry.stageName ?? entry.stage ?? "",
      })),
    );

    addSheet(
      "Aprovacoes Banco",
      approvalsByBankLegend.map((bank) => ({
        Instituicao: bank.bankName,
        Aprovacoes: bank.approvedDecisions.length,
        ValorAprovado: Number(bank.approvedValue ?? 0),
      })),
    );

    addSheet(
      "Decisoes Aprovadas",
      approvalDecisionRows.map((decision) => ({
        Instituicao: decision.bankName,
        Operacao: decision.title,
        Tipo: decision.operationType || "",
        ValorAprovado: Number(decision.approvedValue ?? 0),
        ValorTomado: Number(decision.takenValue ?? 0),
        DataDecisao: decision.date ? formatDecisionDate(decision.date) : "",
      })),
    );

    addSheet(
      "Recusas Amplia",
      amplaDeclineBanks.map((bank) => ({
        Instituicao: bank.bankName,
        Etapa: bank.stage,
        Decisoes: bank.decisions.length,
        ValorRecusado: Number(bank.rejectedValue ?? 0),
        ValorAprovado: Number(bank.approvedValue ?? 0),
      })),
    );

    addSheet(
      "Motivos Recusa",
      declineReasons.map((reason) => ({
        Motivo: reason.reason,
        Quantidade: Number(reason.count ?? 0),
      })),
    );

    addSheet(
      "Tarefas",
      taskCompletion.map((user) => ({
        Usuario: user.userName,
        Atribuidas: Number(user.totalTasks ?? 0),
        Concluidas: Number(user.completedTasks ?? 0),
        ConclusaoPercentual: Number(user.completionRate ?? 0),
      })),
    );

    addSheet(
      "Regioes",
      regionDistribution.map((region) => ({
        Regiao: region.label,
        Leads: Number(region.count ?? 0),
      })),
    );

    addSheet(
      "Segmentos",
      segmentDistribution.map((segment) => ({
        Segmento: segment.label,
        Leads: Number(segment.count ?? 0),
      })),
    );

    const exportDeals = normalizedDealRows.length
      ? normalizedDealRows
      : timelineDealRows.length
        ? timelineDealRows
        : tradesFallbackDetails.length
          ? tradesFallbackDetails.flatMap((day) => day.deals ?? [])
          : filteredTimelineAggregatedDeals;

    addSheet(
      "Negocios",
      exportDeals.map((deal) => ({
        Titulo: deal.title,
        Cliente: deal.client ?? "",
        Email: deal.clientEmail ?? "",
        Telefone: deal.clientPhone ?? "",
        CNPJ: deal.clientCnpj ?? "",
        Socios: deal.clientPartners ?? "",
        Funil: deal.funnel ?? "",
        Etapa: deal.stage ?? "",
        Valor: deal.valueLabel ?? "",
        Data: deal.dateLabel ?? "",
      })),
    );

    addSheet(
      "Pipeline",
      filteredPipelineCardRows.map((card) => ({
        Titulo: card.title,
        Cliente: card.client ?? "",
        Email: card.clientEmail ?? "",
        Telefone: card.clientPhone ?? "",
        CNPJ: card.clientCnpj ?? "",
        Socios: card.clientPartners ?? "",
        Funil: card.funnel ?? "",
        Etapa: card.stage ?? "",
        Valor: card.valueLabel ?? "",
        Data: card.dateLabel ?? "",
      })),
    );

    const fileName = `dashboard-${formatDateKey(new Date())}.xlsx`;
    XLSX.writeFile(workbook, fileName, { compression: true });
  };

  const approvedBankSummaries = useMemo(
    () =>
      approvalsByBank.map((bank) => {
        const decisions = Array.isArray(bank.decisions) ? bank.decisions : [];
        const approvedDecisions = decisions.filter(isDecisionApproved);
        const approvedValue = approvedDecisions.reduce((sum, decision) => {
          const numericValue = toNumber(getDecisionApprovedValue(decision));
          return sum + (numericValue ?? 0);
        }, 0);
        return {
          ...bank,
          approvedDecisions,
          approvedValue,
        };
      }),
    [approvalsByBank],
  );

  const approvedBanks = useMemo(
    () =>
      approvedBankSummaries.filter((bank) => Number(bank.approvedValue) > 0),
    [approvedBankSummaries],
  );

  const computedApprovedValue = approvedBanks.reduce(
    (sum, bank) => sum + Number(bank.approvedValue ?? 0),
    0,
  );
  const computedApprovedCount = approvedBanks.reduce(
    (sum, bank) => sum + bank.approvedDecisions.length,
    0,
  );
  const computedInstitutionCount = approvedBanks.length;

  const kpiApprovedValue = computedApprovedValue;
  const kpiApprovedCount = computedApprovedCount;
  const kpiInstitutionsCount = computedInstitutionCount;

  const approvalDecisionRows = useMemo(() => {
    const decisions = approvedBankSummaries.flatMap((bank) =>
      (bank.approvedDecisions ?? []).map((decision) => ({
        ...decision,
        bankName: bank.bankName,
        bankId: bank.bankId,
      })),
    );

    return decisions
      .sort((a, b) => {
        const aTime = a.decisionAt ? new Date(a.decisionAt).getTime() : 0;
        const bTime = b.decisionAt ? new Date(b.decisionAt).getTime() : 0;
        return bTime - aTime;
      })
      .map((decision) => ({
        key: `${decision.bankId}-${decision.cardId}-${
          decision.decisionAt || ""
        }-${decision.cardTitle || ""}`,
        bankName: decision.bankName || "Instituição",
        title: decision.cardTitle || "Operação",
        approvedValue: pickNumericValue(
          decision.approvedValue,
          decision.approvedAmount,
          decision.amount,
        ),
        takenValue: pickNumericValue(
          decision.takenValue,
          decision.takenAmount,
          decision.drawnValue,
        ),
        date: decision.decisionAt,
        operationType:
          decision.operationType ||
          decision.operation ||
          decision.product ||
          decision.stage ||
          decision.cardType,
      }));
  }, [approvedBankSummaries]);

  const institutionsSummary = formatInstitutionsList(
    approvedBanks.map((bank) => bank.bankName),
  );

  const amplaDeclineBanks = useMemo(() => {
    const source = data?.amplaDeclines ?? [];
    return source
      .map((decline, index) => {
        const decisions =
          Array.isArray(decline.decisions) && decline.decisions.length
            ? decline.decisions
            : [decline];
        const rejectedDecisions = decisions.filter(
          (decision) => !isDecisionApproved(decision),
        );
        const rejectedDecisionsValue = rejectedDecisions.reduce(
          (sum, decision) =>
            sum +
            (toNumber(
              pickNumericValue(
                decision.rejectedValue,
                decision.amount,
                decision.value,
              ),
            ) ?? 0),
          0,
        );
        const rejectedValue =
          pickNumericValue(decline.rejectedValue, decline.rejectedAmount) ??
          rejectedDecisionsValue;
        const approvedValue =
          pickNumericValue(decline.approvedValue, decline.approvedAmount) ?? 0;
        return {
          key:
            decline.bankId ??
            decline.bankName ??
            decline.cardId ??
            decline.id ??
            `ampla-decline-${index}`,
          bankName: decline.bankName || decline.bank || "Amplia Capital",
          decisions,
          rejectedDecisions,
          rejectedValue,
          approvedValue,
          stage: decline.stage || decline.stageName || "—",
        };
      })
      .filter(
        (entry) => entry.rejectedDecisions.length || entry.rejectedValue > 0,
      );
  }, [data?.amplaDeclines]);

  const amplaDeclineDecisionCount = useMemo(
    () =>
      amplaDeclineBanks.reduce(
        (sum, bank) => sum + (bank.rejectedDecisions?.length || 0),
        0,
      ),
    [amplaDeclineBanks],
  );

  const timelineOptions = useMemo(
    () => ({
      chart: {
        type: "bar",
        toolbar: { show: false },
        zoom: { enabled: false },
      },
      plotOptions: {
        bar: {
          columnWidth: "60%",
          borderRadius: 6,
        },
      },
      grid: { borderColor: "#E5E7EB" },
      dataLabels: {
        enabled: true,
        formatter: (val) => Math.round(val).toString(),
        style: { fontSize: "10px", colors: ["#ffffff"] },
        offsetY: -10,
      },
      xaxis: {
        categories: positiveTimelineEntries.map(
          (item) => item.label ?? item.date ?? "",
        ),
        labels: { style: { fontSize: "12px" } },
      },
      yaxis: {
        labels: { formatter: (val) => Math.round(val).toString() },
      },
      colors: [chartColor],
      tooltip: {
        y: { formatter: (val) => `${Math.round(val)} negócios` },
      },
    }),
    [brand, positiveTimelineEntries],
  );

  const timelineSeries = useMemo(
    () => [
      {
        name: "Negócios iniciados",
        data: positiveTimelineEntries.map((item) => item.count),
      },
    ],
    [positiveTimelineEntries],
  );

  const stageTotalsOptions = useMemo(
    () => ({
      chart: { type: "line", toolbar: { show: false } },
      stroke: { width: [0, 3], curve: "smooth" },
      plotOptions: {
        bar: { columnWidth: "50%", borderRadius: 6 },
      },
      dataLabels: {
        enabled: true,
        enabledOnSeries: [0],
        formatter: (value) => Math.round(value).toString(),
        style: { fontSize: "10px", colors: ["#ffffff"] },
        offsetY: -6,
      },
      xaxis: {
        categories: stageDurations.map((stage) => stage.stageName),
        labels: { style: { fontSize: "12px" } },
      },
      yaxis: [
        {
          title: { text: "Negócios" },
          labels: { formatter: (value) => Math.round(value).toString() },
        },
        {
          opposite: true,
          title: { text: "Dias médios" },
          labels: { formatter: (value) => `${Number(value).toFixed(1)}d` },
        },
      ],
      grid: { borderColor: "#E5E7EB" },
      colors: [chartColor, brand],
      tooltip: {
        shared: true,
        intersect: false,
        y: {
          formatter: (val, opts) => {
            if (opts.seriesIndex === 0) {
              return `${Math.round(val)} negócios`;
            }
            return `${Number(val).toFixed(1)} dias`;
          },
        },
      },
    }),
    [brand, chartColor, stageDurations],
  );

  const stageTotalsSeries = useMemo(
    () => [
      {
        name: "Negócios",
        type: "column",
        data: stageDurations.map((stage) => Number(stage.entries ?? 0)),
      },
      {
        name: "Dias médios",
        type: "line",
        data: stageDurations.map((stage) => Number(stage.averageDays ?? 0)),
      },
    ],
    [stageDurations],
  );

  const regionSeries = regionDistribution.map((item) => item.count);
  const regionLabels = regionDistribution.map((item) => item.label);
  const segmentSeries = segmentDistribution.map((item) => item.count);
  const segmentLabels = segmentDistribution.map((item) => item.label);

  const handleRegionDataPointSelection = (dataPointIndex) => {
    const label = regionLabels[dataPointIndex];
    if (!label) {
      return;
    }
    setSelectedRegionLabel(label);
    setIsRegionModalOpen(true);
  };

  const approvalsSeries = approvedBanks.map((bank) =>
    Number(bank.approvedValue ?? 0),
  );
  const approvalsLabels = approvedBanks.map(
    (bank) => bank.bankName || "Instituição",
  );
  const hasApprovalsData = approvalsSeries.some((value) => value > 0);
  const approvalsByBankLegend = approvedBanks
    .slice()
    .sort(
      (a, b) =>
        (Number(b.approvedValue ?? 0) || 0) -
        (Number(a.approvedValue ?? 0) || 0),
    );

  const regionOptions = {
    chart: {
      type: "donut",
      toolbar: { show: false },
      events: {
        dataPointSelection: (_event, _chart, config) => {
          const index = config?.dataPointIndex ?? -1;
          if (index < 0) return;
          handleRegionDataPointSelection(index);
        },
      },
    },
    labels: regionLabels,
    colors: regionPalette,
    legend: { position: "bottom" },
    dataLabels: { enabled: false },
    tooltip: { y: { formatter: (val) => `${val} leads` } },
  };

  const segmentOptions = {
    chart: { type: "donut", toolbar: { show: false } },
    labels: segmentLabels,
    colors: chartPalette,
    legend: { position: "bottom" },
    dataLabels: { enabled: false },
    tooltip: { y: { formatter: (val) => `${val} leads` } },
  };

  const approvalsOptions = {
    chart: { type: "donut", toolbar: { show: false } },
    labels: approvalsLabels,
    colors: approvalPalette,
    dataLabels: { enabled: false },
    legend: { position: "bottom" },
    tooltip: {
      y: {
        formatter: (val) => (val > 0 ? currency(val) : "Sem valor aprovado"),
      },
    },
    responsive: [
      {
        breakpoint: 1024,
        options: {
          legend: { position: "bottom" },
        },
      },
    ],
  };

  const getApprovalPaletteColor = (index) =>
    approvalPalette[index % approvalPalette.length];

  const summaryCards = [
    {
      label: "Negócios iniciados",
      value: dealsStartedMetricCount,
      note: rangeLabel,
      accent: "from-rose-500/60 to-orange-400/70",
      icon: <HiArrowPath className="text-xl text-white/80" />,
    },
    {
      label: "Valor aprovado",
      value: currency(kpiApprovedValue),
      note: `${kpiInstitutionsCount} instituições`,
      accent: "from-emerald-500/70 to-emerald-700/70",
      icon: <HiOutlineShieldCheck className="text-xl text-white/80" />,
    },
    {
      label: "Valor reprovado",
      value: currency(summary.rejectedValue),
      note: "Baseado nas decisões",
      accent: "from-amber-400/80 to-amber-500/70",
      icon: <HiChartBar className="text-xl text-white/80" />,
    },
    {
      label: "Declinações Amplia",
      value: amplaDeclineDecisionCount,
      note: "motivos registrados",
      accent: "from-slate-600/80 to-slate-700/90",
      icon: <HiSparkles className="text-xl text-white/80" />,
    },
  ];

  const heroMetrics = [
    {
      label: "Cards em pipeline",
      value: pipelineLoading
        ? "..."
        : pipelineError
          ? "—"
          : filteredPipelineCardRows.length,
      contrast: pipelineError ? "Erro ao carregar" : "Todos os funis",
      icon: <HiArrowPath className="text-2xl text-white" />,
    },
    {
      label: "Negócios iniciados",
      value: dealsStartedMetricCount,
      contrast: `${rangeLabel}`,
      icon: <HiSparkles className="text-2xl text-white" />,
    },
    {
      label: "Valor aprovado",
      value: currency(kpiApprovedValue),
      contrast: `${kpiApprovedCount} aprovações`,
      icon: <HiShieldCheck className="text-2xl text-white" />,
      detailList: approvalDecisionRows,
      institutionsSummary,
    },
    {
      label: "Declinações Amplia",
      value: amplaDeclineDecisionCount,
      contrast: "motivação registrada",
      icon: <HiChartBar className="text-2xl text-white" />,
    },
  ];

  const aiAnalysisDraft = useMemo(() => {
    const approvalRate =
      dealsStartedMetricCount > 0
        ? (kpiApprovedCount / dealsStartedMetricCount) * 100
        : null;
    const declineRate =
      dealsStartedMetricCount > 0
        ? (amplaDeclineDecisionCount / dealsStartedMetricCount) * 100
        : null;
    const topRegion = regionDistribution[0] ?? null;
    const topSegment = segmentDistribution[0] ?? null;
    const slowStages = [...stageDurations]
      .sort((a, b) => Number(b.averageDays ?? 0) - Number(a.averageDays ?? 0))
      .slice(0, 3)
      .map((stage) => ({
        name: stage.stageName,
        days: Number(stage.averageDays ?? 0),
        entries: Number(stage.entries ?? 0),
      }));

    const executiveSummary = [
      approvalRate != null
        ? `A taxa de aprovação estimada no período está em ${approvalRate.toFixed(
            1,
          )}% (${kpiApprovedCount} aprovações para ${dealsStartedMetricCount} negócios iniciados).`
        : "Não foi possível calcular a taxa de aprovação com os dados atuais.",
      topRegion
        ? `A região com maior concentração é ${topRegion.label}, com ${topRegion.count} leads.`
        : "Não há concentração regional suficiente para apontar uma região dominante.",
      topSegment
        ? `O segmento mais representativo no recorte atual é ${topSegment.label} (${topSegment.count} leads).`
        : "Não foi identificado segmento dominante no período selecionado.",
      `O volume financeiro mostra ${currency(
        kpiApprovedValue,
      )} em valores aprovados e ${currency(
        summary.rejectedValue,
      )} em valores recusados.`,
    ];

    const attentionPoints = [];

    if (declineRate != null && declineRate >= 30) {
      attentionPoints.push(
        `A taxa de declínio da Amplia está elevada (${declineRate.toFixed(
          1,
        )}%), sugerindo necessidade de revisão de qualificação inicial.`,
      );
    }

    if (leadConversionRate != null && leadConversionRate < 20) {
      attentionPoints.push(
        `A conversão de leads está em ${leadConversionRate.toFixed(
          1,
        )}%, abaixo da zona saudável para escala comercial.`,
      );
    }

    if (slowStages[0]?.days >= 7) {
      attentionPoints.push(
        `A etapa "${slowStages[0].name}" apresenta maior tempo médio (${slowStages[0].days.toFixed(
          1,
        )} dias), com impacto direto no ciclo de fechamento.`,
      );
    }

    if (!attentionPoints.length) {
      attentionPoints.push(
        "Não foram detectados gargalos críticos no recorte atual; mantenha monitoramento contínuo para validar tendência.",
      );
    }

    const recommendations = [
      slowStages.length
        ? `Priorizar plano de redução de SLA nas etapas mais lentas: ${slowStages
            .map((item) => item.name)
            .join(", ")}.`
        : "Consolidar histórico de etapas para permitir detecção de gargalos operacionais.",
      approvalRate != null && approvalRate < 35
        ? "Ajustar critérios de entrada no funil e reforçar pré-qualificação para elevar taxa de aprovação."
        : "Manter critérios atuais de aprovação e testar pequenos incrementos de volume mantendo qualidade.",
      amplaDeclineDecisionCount > 0
        ? "Revisar os principais motivos de recusa por instituição e criar tratativas comerciais por motivo recorrente."
        : "Mapear causas de recusa mesmo em baixa volumetria para prevenir deterioração futura.",
    ];

    return {
      generatedAt: formatGeneratedAtLocal(Date.now()),
      executiveSummary,
      attentionPoints,
      recommendations,
      slowStages,
      indicators: {
        dealsStarted: dealsStartedMetricCount,
        conversionRate: leadConversionRate,
        approvedValue: kpiApprovedValue,
        rejectedValue: summary.rejectedValue,
        declines: amplaDeclineDecisionCount,
      },
    };
  }, [
    dealsStartedMetricCount,
    kpiApprovedCount,
    amplaDeclineDecisionCount,
    regionDistribution,
    segmentDistribution,
    kpiApprovedValue,
    summary.rejectedValue,
    leadConversionRate,
    stageDurations,
  ]);

  const handleGenerateAiAnalysis = async () => {
    if (isLoading || waitingForFunnelSelection) {
      return;
    }
    setIsGeneratingAiAnalysis(true);
    setAiAnalysisError(null);

    try {
      const analysis = await fetchOperationsDashboardAnalysis({
        ...periodParams,
        funnelIds: selectedFunnelIds,
      });
      if (analysis) {
        const generatedAt = formatGeneratedAtLocal(
          analysis.generatedAtIso || analysis.generatedAt,
        );
        setAiAnalysisSnapshot({
          ...analysis,
          generatedAt,
        });
      } else {
        setAiAnalysisSnapshot(aiAnalysisDraft);
        setAiAnalysisError(
          "Resposta da análise vazia; exibindo fallback local.",
        );
      }
    } catch (error) {
      setAiAnalysisSnapshot(aiAnalysisDraft);
      setAiAnalysisError(
        error?.response?.data?.message ||
          "Falha ao gerar análise no backend; exibindo fallback local.",
      );
    } finally {
      setIsAiAnalysisModalOpen(true);
      setIsGeneratingAiAnalysis(false);
    }
  };

  const ApprovalDecisionList = ({
    list = [],
    institutionsSummary: institutions,
    maxVisible = 3,
  }) => {
    const visibleList =
      typeof maxVisible === "number" && maxVisible >= 0
        ? list.slice(0, maxVisible)
        : list;

    if (!visibleList.length) {
      return null;
    }

    return (
      <div className="mt-3 space-y-3">
        {institutions ? (
          <p className="text-[11px]">Instituições liberaram: {institutions}</p>
        ) : null}
        <div className="space-y-3 max-h-[220px] overflow-y-auto px-2 bg-gray-400 py-3 rounded-lg">
          {visibleList.map((decision) => (
            <div
              key={decision.key}
              className="rounded-2xl border border-white/15 bg-white/5 p-3"
            >
              <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.3em] text-white/60">
                <span>{decision.bankName}</span>
                <span>{formatDecisionDate(decision.date)}</span>
              </div>
              <p className="text-xs font-semibold text-white mt-1">
                {decision.title}
              </p>
              <div className="flex flex-wrap gap-3 text-[11px] text-white/80 mt-1">
                <span>
                  Aprovado {formatCurrencyOrPlaceholder(decision.approvedValue)}
                </span>
              </div>
            </div>
          ))}
        </div>
        {list.length > visibleList.length ? (
          <p className="text-[11px] text-white/50">
            Mostrando {visibleList.length} de {list.length}
          </p>
        ) : null}
      </div>
    );
  };

  const AmpliaDeclinesPanel = ({ banks = [], declineReasons = [] }) => {
    const [expandedBanks, setExpandedBanks] = useState(() => new Set());
    const hasBanks = banks.length > 0;
    const toggleBank = (key) =>
      setExpandedBanks((previous) => {
        const next = new Set(previous);
        if (next.has(key)) {
          next.delete(key);
        } else {
          next.add(key);
        }
        return next;
      });
    const declineReasonsLabels = useMemo(
      () => declineReasons.map((reason) => reason.reason || "—"),
      [declineReasons],
    );
    const declineReasonsSeries = useMemo(
      () => [
        {
          name: "Recusas",
          data: declineReasons.map((reason) => Number(reason.count ?? 0)),
        },
      ],
      [declineReasons],
    );
    const declineReasonsHeight = useMemo(
      () => Math.min(360, 140 + declineReasons.length * 26),
      [declineReasons.length],
    );
    const declineReasonsOptions = useMemo(
      () => ({
        chart: { type: "bar", toolbar: { show: false } },
        plotOptions: {
          bar: { horizontal: true, barHeight: "60%", borderRadius: 6 },
        },
        grid: { borderColor: "#E5E7EB" },
        dataLabels: {
          enabled: true,
          formatter: (value) => Math.round(value).toString(),
          style: { fontSize: "10px", colors: ["#ffffff"] },
          offsetX: 8,
        },
        xaxis: {
          categories: declineReasonsLabels,
          labels: { style: { fontSize: "11px" } },
        },
        yaxis: { labels: { style: { fontSize: "11px" } } },
        colors: [brand],
        tooltip: {
          y: { formatter: (value) => `${Math.round(value)} recusas` },
        },
      }),
      [declineReasonsLabels, brand],
    );

    return (
      <>
        {hasBanks ? (
          <div className="space-y-4">
            {banks.map((bank, index) => {
              const isExpanded = expandedBanks.has(bank.key);
              const detailId = `ampla-decline-${index}`;
              return (
                <div
                  key={bank.key}
                  className="rounded-2xl border border-slate-100 bg-white shadow-sm p-4 space-y-3"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-sm font-semibold text-gray-900">
                        {bank.bankName}
                      </p>
                      <p className="text-[11px] text-gray-500">
                        {bank.decisions.length} decisões
                      </p>
                    </div>
                    <div className="text-right space-y-2">
                      <div>
                        <p className="text-sm font-semibold text-rose-600">
                          {currency(bank.rejectedValue)}
                        </p>
                        {bank.approvedValue ? (
                          <p className="text-[11px] text-gray-500">
                            {currency(bank.approvedValue)} aprovados
                          </p>
                        ) : null}
                      </div>
                      <button
                        type="button"
                        className="text-[11px] font-semibold text-gray-500 hover:text-gray-800 transition-colors"
                        onClick={() => toggleBank(bank.key)}
                        aria-expanded={isExpanded}
                        aria-controls={detailId}
                      >
                        {isExpanded ? "Ocultar detalhes" : "Ver detalhes"}
                      </button>
                    </div>
                  </div>
                  {isExpanded ? (
                    <div id={detailId} className="space-y-3">
                      {bank.rejectedDecisions.length ? (
                        bank.rejectedDecisions.map((decision) => (
                          <div
                            key={`${bank.key}-${decision.cardId}-${decision.decisionAt}`}
                            className="rounded-2xl border border-slate-100 bg-slate-50/80 p-3"
                          >
                            <div className="flex items-center justify-between">
                              <p className="text-sm font-semibold text-gray-900">
                                {decision.cardTitle ||
                                  decision.title ||
                                  "Negócio"}
                              </p>
                              <span className="text-[11px] text-gray-500">
                                {formatDecisionDate(decision.decisionAt)}
                              </span>
                            </div>
                            <p className="text-[11px] text-gray-500">
                              {decision.stage || bank.stage || "—"}
                            </p>
                            <p className="text-sm font-semibold text-rose-600 mt-1">
                              Recusado{" "}
                              {formatCurrencyOrPlaceholder(
                                pickNumericValue(
                                  decision.rejectedValue,
                                  decision.amount,
                                  decision.value,
                                ),
                              )}
                            </p>
                            {decision.reason ? (
                              <p className="text-[11px] text-gray-600 mt-1">
                                {decision.reason}
                              </p>
                            ) : null}
                          </div>
                        ))
                      ) : (
                        <p className="text-[11px] text-gray-500">
                          Recusas registradas sem detalhes individuais.
                        </p>
                      )}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="py-10 text-center text-sm text-gray-500">
            Nenhuma recusa registrada pela Amplia Capital.
          </div>
        )}
      </>
    );
  };

  const SummaryStatCard = ({ card }) => (
    <div
      className={[
        "relative rounded-3xl p-5 shadow-md overflow-hidden border border-white/20",
        "bg-gradient-to-br text-white",
        card.accent,
      ].join(" ")}
    >
      <div className="flex items-center justify-between mb-3">
        <p className="text-[11px] uppercase tracking-[0.3em] text-white/70">
          {card.label}
        </p>
        <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-white/20">
          {card.icon}
        </div>
      </div>
      <p className="text-3xl font-semibold leading-snug">{card.value}</p>
      <p className="text-[11px] text-white/70 mt-2">{card.note}</p>
      {card.detailList ? (
        <ApprovalDecisionList
          list={card.detailList}
          institutionsSummary={card.institutionsSummary}
        />
      ) : null}
      <div className="absolute -top-4 -right-4 h-20 w-20 rounded-full bg-white/10 blur-2xl" />
    </div>
  );

  const DashboardSkeleton = () => (
    <div className="space-y-4 animate-pulse">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {Array.from({ length: 3 }).map((_, index) => (
          <div
            key={`skeleton-hero-${index}`}
            className="h-28 rounded-3xl border border-gray-200 bg-gray-200"
          />
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {Array.from({ length: 2 }).map((_, index) => (
          <div
            key={`skeleton-chart-${index}`}
            className="h-80 rounded-3xl border border-gray-200 bg-gray-200"
          />
        ))}
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {Array.from({ length: 2 }).map((_, index) => (
          <div
            key={`skeleton-table-${index}`}
            className="h-72 rounded-3xl border border-gray-200 bg-gray-200"
          />
        ))}
      </div>
    </div>
  );

  const fancyCardClasses =
    "border border-gray-100 bg-white shadow-md rounded-3xl p-5 flex flex-col gap-2";

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45 }}
        className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white rounded-3xl p-6 border border-white/10 shadow-md relative overflow-hidden min-h-[220px]"
      >
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
          <div className="flex flex-col gap-1 md:max-w-lg xl:max-w-xl">
            <p className="text-xs uppercase tracking-[0.4em] text-white/70">
              Operações Corporativas
            </p>
            <div className="flex flex-wrap items-baseline gap-2">
              <h2 className="font-semibold text-3xl md:text-4xl">
                {selectedFunnelHeading}
              </h2>
            </div>
            <p className="text-sm text-white/70 max-w-2xl leading-relaxed">
              Métricas filtradas por quadro e período selecionado. Os cartões
              abaixo trazem aprovações, recusas e tempos em cada etapa para o
              time responsável.
            </p>
          </div>
          <div>
            <div className="flex flex-wrap gap-3 text-slate-200">
              <div
                className="flex flex-col text-xs uppercase tracking-[0.4em] gap-2"
                style={{ maxWidth: "300px" }}
              >
                <span className="text-white/70">Quadros</span>
                <select
                  value={funnelSelectionValue}
                  onChange={handleFunnelSelectChange}
                  className="border border-white/40 rounded-lg px-3 py-2 text-sm bg-white text-slate-900"
                  disabled={funnelsLoading || noFunnelConfigured}
                >
                  <option value="">
                    {funnelsLoading
                      ? "Carregando..."
                      : noFunnelConfigured
                        ? "Nenhum quadro"
                        : "Selecione um quadro"}
                  </option>
                  {funnels.map((funnel) => (
                    <option
                      key={funnel.id}
                      value={funnel.id}
                      disabled={selectedFunnelIds.includes(funnel.id)}
                    >
                      {funnel.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col text-xs uppercase tracking-[0.4em] gap-2">
                <span className="text-white/70">Período</span>
                <DatePicker
                  selectsRange
                  startDate={rangeStart}
                  endDate={rangeEnd}
                  onChange={handleDateRangeChange}
                  maxDate={new Date()}
                  locale="pt-BR"
                  dateFormat="dd/MM/yyyy"
                  placeholderText="Selecione um período"
                  className="border border-white/40 rounded-lg px-3 py-2 text-sm bg-white text-slate-900 w-[220px]"
                  popperClassName="z-[9999]"
                  popperPlacement="bottom-start"
                  popperContainer={({ children }) =>
                    createPortal(children, document.body)
                  }
                  renderCustomHeader={({
                    date,
                    decreaseMonth,
                    increaseMonth,
                    prevMonthButtonDisabled,
                    nextMonthButtonDisabled,
                  }) => (
                    <div className="flex items-center justify-between px-2 pb-2">
                      <button
                        type="button"
                        onClick={decreaseMonth}
                        disabled={prevMonthButtonDisabled}
                        className="text-slate-500 hover:text-slate-800 disabled:opacity-40"
                      >
                        ‹
                      </button>
                      <button
                        type="button"
                        onClick={() => handleMonthRangePick(date)}
                        className="text-[11px] font-semibold uppercase tracking-[0.3em] text-slate-500 hover:text-slate-900"
                      >
                        {date.toLocaleDateString("pt-BR", {
                          month: "long",
                          year: "numeric",
                        })}
                      </button>
                      <button
                        type="button"
                        onClick={increaseMonth}
                        disabled={nextMonthButtonDisabled}
                        className="text-slate-500 hover:text-slate-800 disabled:opacity-40"
                      >
                        ›
                      </button>
                    </div>
                  )}
                />
              </div>
              {isPowerUser ? (
                <div className="flex flex-col text-xs uppercase tracking-[0.4em] gap-2">
                  <span className="text-white/70">Exportar</span>
                  <Button
                    color="light"
                    size="sm"
                    onClick={handleExportDashboard}
                    disabled={!canExportDashboard}
                    className="!bg-white !text-slate-900 border border-white/40 hover:!bg-slate-100 disabled:opacity-60"
                  >
                    Baixar planilha
                  </Button>
                </div>
              ) : null}
            </div>
            <div className="mt-2">
              <p className="text-[11px] text-white/60 mt-1 tracking-[0.3em]">
                Quadros selecionados
              </p>
              <div className="flex flex-wrap gap-2 mt-2">
                {selectedFunnels.length ? (
                  selectedFunnels.map((funnel) => (
                    <button
                      key={funnel.id}
                      type="button"
                      onClick={() => toggleFunnelSelection(funnel.id)}
                      className="rounded-full bg-white text-slate-900 px-3 py-1 text-[11px] font-medium"
                    >
                      {funnel.name}
                    </button>
                  ))
                ) : (
                  <span className="text-[11px] text-white/50">
                    Nenhum quadro selecionado
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {waitingForFunnelSelection && (
          <p className="text-[11px] text-white/60 mt-3">
            Selecione ao menos um quadro para visualizar os dados.
          </p>
        )}
        {funnelsError && (
          <p className="text-[11px] text-rose-200 mt-3">
            Falha ao carregar os quadros: {funnelsErrorObj?.message || "erro"}
          </p>
        )}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
          {heroMetrics.map((metric) => {
            const isDealsMetric = metric.label === "Negócios iniciados";
            const isPipelineMetric = metric.label === "Cards em pipeline";
            const isApprovedMetric = metric.label === "Valor aprovado";
            const isDeclineMetric = metric.label === "Declinações Amplia";
            const isClickable =
              (isDealsMetric && hasDealsData) ||
              (isPipelineMetric && hasPipelineCards) ||
              (isApprovedMetric && hasApprovalsData) ||
              (isDeclineMetric && amplaDeclineBanks.length > 0);
            const onClickHandler = isDealsMetric
              ? handleDealsCardClick
              : isPipelineMetric
                ? handlePipelineCardClick
                : isApprovedMetric
                  ? handleApprovalsCardClick
                  : isDeclineMetric
                    ? handleAmpliaDeclinesCardClick
                    : undefined;
            const clickableAriaLabel = isDealsMetric
              ? "Ver negócios iniciados"
              : isPipelineMetric
                ? "Ver cards em pipeline"
                : isApprovedMetric
                  ? "Detalhes dos valores aprovados"
                  : isDeclineMetric
                    ? "Ver recusas da Amplia Capital"
                    : undefined;
            return (
              <div
                key={metric.label}
                role={isClickable ? "button" : undefined}
                tabIndex={isClickable ? 0 : undefined}
                onClick={isClickable ? onClickHandler : undefined}
                onKeyDown={
                  isClickable
                    ? (event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          onClickHandler?.();
                        }
                      }
                    : undefined
                }
                className={`bg-white/10 border border-white/20 rounded-2xl p-4 backdrop-blur shadow-md ${
                  isClickable
                    ? "cursor-pointer transition duration-200 hover:shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
                    : ""
                }`}
                aria-label={clickableAriaLabel}
              >
                <div className="flex items-center justify-between text-white mb-2">
                  <p className="text-[11px] uppercase tracking-[0.3em] text-white/70">
                    {metric.label}
                  </p>
                  <div className="flex items-center gap-2">
                    <span>{metric.icon}</span>
                    {isClickable ? (
                      <HiArrowTopRightOnSquare className="text-xs text-white/70" />
                    ) : null}
                  </div>
                </div>
                <p className="text-3xl font-semibold leading-none">
                  {metric.value}
                </p>
                <p className="text-[11px] text-white/70 mt-2">
                  {metric.contrast}
                </p>
              </div>
            );
          })}
        </div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.25),_transparent_40%)] pointer-events-none" />
      </motion.div>

      {isError && (
        <div className="bg-red-50 border border-red-100 rounded-2xl p-4 text-sm text-red-700">
          <span>
            Falha ao carregar o painel: {error?.message || "Erro desconhecido"}
          </span>
        </div>
      )}

      {hasFunnelSelection ? (
        isLoading && !data ? (
          <DashboardSkeleton />
        ) : (
          <>
            <div className="rounded-3xl border border-[#A60B3E]/20 bg-gradient-to-br from-[#fff5f8] via-white to-[#f8fafc] p-6 shadow-md">
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div className="space-y-2">
                  <p className="text-[11px] uppercase tracking-[0.3em] text-[#A60B3E]">
                    INTEGRAÇÃO DE I.A PARA ANÁLISE DE DADOS / INDICADORES
                  </p>
                  <h2 className="text-xl font-bold text-gray-900">
                    Assistente de análise detalhada
                  </h2>
                  <p className="text-sm text-gray-600 max-w-3xl">
                    Gere uma leitura executiva dos principais indicadores do
                    relatório atual, com pontos de atenção e recomendações
                    práticas para o time.
                  </p>
                </div>
                <div className="flex flex-col items-start md:items-end gap-2">
                  <Button
                    color="pink"
                    onClick={handleGenerateAiAnalysis}
                    disabled={isGeneratingAiAnalysis || isLoading}
                    className="!bg-[#A60B3E] hover:!bg-[#8f0935]"
                  >
                    <span className="inline-flex items-center gap-2 text-white">
                      <HiOutlineSparkles className="text-base" />
                      {isGeneratingAiAnalysis
                        ? "Gerando análise..."
                        : "Gerar análise detalhada"}
                    </span>
                  </Button>
                  {aiAnalysisSnapshot ? (
                    <span className="text-[11px] text-gray-500">
                      Última análise gerada em {aiAnalysisSnapshot.generatedAt}
                    </span>
                  ) : (
                    <span className="text-[11px] text-gray-500">
                      Clique para gerar uma leitura com base no período atual.
                    </span>
                  )}
                  {aiAnalysisError ? (
                    <span className="text-[11px] text-amber-700">
                      {aiAnalysisError}
                    </span>
                  ) : null}
                </div>
              </div>
            </div>

            <div className="bg-white rounded-3xl border border-gray-100 shadow-md p-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">
                    Conversão de leads
                  </h2>
                  <p className="text-xs text-gray-500">
                    Percentual de leads do período que viraram negócios
                  </p>
                </div>
                <Badge color="light" className="!text-gray-700">
                  {rangeLabel}
                </Badge>
              </div>
              <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="rounded-2xl border border-gray-100 bg-gray-50 p-4">
                  <p className="text-[11px] uppercase tracking-[0.3em] text-gray-500">
                    Conversão
                  </p>
                  <p className="text-3xl font-semibold text-gray-900 mt-2">
                    {leadConversionRate != null
                      ? `${leadConversionRate.toFixed(1)}%`
                      : "—"}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {leadsTotal > 0
                      ? `${leadsConverted} de ${leadsTotal} leads`
                      : "Sem leads no período"}
                  </p>
                </div>
                <div className="rounded-2xl border border-gray-100 bg-white p-4">
                  <p className="text-[11px] uppercase tracking-[0.3em] text-gray-500">
                    Leads no período
                  </p>
                  <p className="text-3xl font-semibold text-gray-900 mt-2">
                    {leadsTotal}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Base de cálculo da conversão
                  </p>
                </div>
                <div className="rounded-2xl border border-gray-100 bg-white p-4">
                  <p className="text-[11px] uppercase tracking-[0.3em] text-gray-500">
                    Leads convertidos
                  </p>
                  <p className="text-3xl font-semibold text-gray-900 mt-2">
                    {leadsConverted}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Leads que viraram negócios
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="bg-white rounded-3xl border border-gray-100 shadow-md p-6 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-bold text-gray-900">
                      Faturamento anual
                    </h2>
                    <p className="text-xs text-gray-500">
                      Faturamento médio dos leads no período
                    </p>
                  </div>
                  <Badge color="light" className="!text-gray-700">
                    {rangeLabel}
                  </Badge>
                </div>
                <p className="text-3xl font-semibold text-gray-900">
                  {currency(summary.averageLeadPresumedRevenue ?? 0)}
                </p>
                <p className="text-sm text-gray-500">
                  {dealsStartedMetricCount} negócios iniciados
                </p>
                <p className="text-sm text-gray-500">
                  Média de faturamento presumido dos leads (baseado em{" "}
                  {summary.leadPresumedRevenueCount ?? 0} clientes)
                </p>
                {dealsStartedMetricCount >
                (summary.leadPresumedRevenueCount ?? 0) ? (
                  <p className="text-xs text-gray-400">
                    Sem informações de faturamento presumido para{" "}
                    {dealsStartedMetricCount -
                      (summary.leadPresumedRevenueCount ?? 0)}{" "}
                    negócios iniciados.
                  </p>
                ) : null}
              </div>

              <div className="bg-white rounded-3xl border border-gray-100 shadow-md p-6 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-bold text-gray-900">Estado</h2>
                    <p className="text-xs text-gray-500">
                      Concentração dos leads por estado
                    </p>
                  </div>
                  <Badge color="light" className="!text-gray-700">
                    Top {regionDistribution.length}
                  </Badge>
                </div>
                {regionSeries.length ? (
                  <ReactApexChart
                    options={regionOptions}
                    series={regionSeries}
                    type="donut"
                    height={200}
                  />
                ) : (
                  <div className="py-8 text-sm text-center text-gray-500">
                    Sem dados de estado para o período.
                  </div>
                )}
                {regionDistribution.length ? (
                  <div className="space-y-2 text-sm text-gray-500 border-t border-dashed border-gray-200 pt-3">
                    {regionDistribution.slice(0, 3).map((region) => (
                      <div
                        key={region.label}
                        className="flex items-center justify-between"
                      >
                        <span>{region.label}</span>
                        <span className="font-semibold text-gray-900">
                          {region.count}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>

              <div className="bg-white rounded-3xl border border-gray-100 shadow-md p-6 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-bold text-gray-900">
                      Segmento
                    </h2>
                    <p className="text-xs text-gray-500">
                      Quais são os principais segmentos dos leads
                    </p>
                  </div>
                  <Badge color="light" className="!text-gray-700">
                    Top {segmentDistribution.length}
                  </Badge>
                </div>
                {segmentSeries.length ? (
                  <ReactApexChart
                    options={segmentOptions}
                    series={segmentSeries}
                    type="donut"
                    height={200}
                  />
                ) : (
                  <div className="py-8 text-sm text-center text-gray-500">
                    Sem dados de segmento para o período.
                  </div>
                )}
                {segmentDistribution.length ? (
                  <div className="space-y-2 text-sm text-gray-500 border-t border-dashed border-gray-200 pt-3">
                    {segmentDistribution.slice(0, 3).map((segment) => (
                      <div
                        key={segment.label}
                        className="flex items-center justify-between"
                      >
                        <span>{segment.label}</span>
                        <span className="font-semibold text-gray-900">
                          {segment.count}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              <div className="bg-white rounded-3xl border border-gray-100 shadow-md p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-bold text-gray-900">
                      Negócios iniciados
                    </h2>
                    <p className="text-xs text-gray-500">
                      Evolução diária · {selectedFunnelTimelineLabel}
                    </p>
                  </div>
                  <Badge color="light" className="!text-gray-700">
                    {positiveTimelineEntries.length} dias ativos
                  </Badge>
                </div>
                {timeline.length ? (
                  <ReactApexChart
                    options={timelineOptions}
                    series={timelineSeries}
                    type="bar"
                    height={300}
                  />
                ) : (
                  <div className="py-10 text-center text-sm text-gray-500">
                    Sem negócios registrados nesse período.
                  </div>
                )}
              </div>

              <div className="bg-white rounded-3xl border border-gray-100 shadow-md p-5 space-y-4">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">
                    Negócios por etapa
                  </h2>
                  <p className="text-xs text-gray-500">
                    Total de negócios e tempo médio por etapa
                  </p>
                </div>
                {stageDurations.length ? (
                  <>
                    <ReactApexChart
                      options={stageTotalsOptions}
                      series={stageTotalsSeries}
                      type="line"
                      height={260}
                    />
                    <div className="space-y-2">
                      {stageDurations.slice(0, 4).map((stage) => {
                        const avgDays = Number(stage.averageDays ?? 0);
                        const avgHours = Number(stage.averageHours ?? 0);
                        return (
                          <div
                            key={stage.stageKey ?? stage.stageId}
                            className="flex items-center justify-between text-sm"
                          >
                            <div>
                              <p className="font-semibold text-gray-900">
                                {stage.stageName}
                              </p>
                              <p className="text-[11px] text-gray-500">
                                {stage.entries} negócios
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-gray-900">
                                {avgDays.toFixed(1)}d
                              </p>
                              <p className="text-[11px] text-gray-500">
                                ≈ {avgHours.toFixed(1)}h
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                ) : (
                  <div className="py-10 text-center text-sm text-gray-500">
                    Ainda não há trajetórias completas registradas.
                  </div>
                )}
              </div>
            </div>

            {/* <div className="bg-white rounded-3xl border border-gray-100 shadow-md p-6 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">
                    Efetividade por etapa
                  </h2>
                  <p className="text-xs text-gray-500">
                    Percentual que chega até aprovação a partir de cada etapa
                  </p>
                </div>
                <Badge color="light" className="!text-gray-700">
                  {approvalStages.length
                    ? `${approvalStages.length} etapa(s) alvo`
                    : "Sem etapa alvo"}
                </Badge>
              </div>
              {stageDurationsWithEffectiveness.length ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {stageDurationsWithEffectiveness.map((stage) => {
                    const rate =
                      stage.effectivenessRate != null
                        ? Number(stage.effectivenessRate)
                        : null;
                    const tone = getEffectivenessTone(rate);
                    return (
                      <div
                        key={`effectiveness-${stage.stageKey ?? stage.stageId}`}
                        className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="text-sm font-semibold text-gray-900">
                              {stage.stageName}
                            </p>
                            <p className="text-[11px] text-gray-500">
                              {stage.entries} negócios
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-semibold text-gray-900 leading-none">
                              {rate != null ? `${rate.toFixed(1)}%` : "—"}
                            </p>
                            <p
                              className={`text-[11px] font-semibold ${tone.text}`}
                            >
                              {tone.label}
                            </p>
                          </div>
                        </div>
                        <div className="mt-3 h-2.5 rounded-full bg-gray-100 overflow-hidden">
                          <div
                            className={`h-full ${tone.bar} transition-all duration-300`}
                            style={{ width: `${rate ?? 0}%` }}
                          />
                        </div>
                        <div className="mt-2 flex items-center justify-between text-[11px] text-gray-400">
                          <span>0%</span>
                          <span>100%</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="py-8 text-center text-sm text-gray-500">
                  Ainda não há trajetórias completas registradas.
                </div>
              )}
              {!approvalStages.length ? (
                <p className="text-[11px] text-amber-600">
                  Nenhuma etapa de aprovação identificada (nome contendo
                  “aprov/approved/approval”).
                </p>
              ) : (
                <p className="text-[11px] text-gray-400">
                  Cálculo: entradas na etapa de aprovação ÷ entradas na etapa.
                </p>
              )}
            </div> */}

            <div className="bg-white rounded-3xl border border-gray-100 shadow-md p-6 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">
                    Tarefas concluídas por usuário
                  </h2>
                  <p className="text-xs text-gray-500">
                    Total de tarefas criadas e concluídas no período por
                    responsável.
                  </p>
                </div>
                <Badge color="light" className="!text-gray-700">
                  Total {taskCompletion.length}
                </Badge>
              </div>
              {taskCompletion.length ? (
                <div className="space-y-4">
                  {taskCompletion.map((user) => (
                    <div
                      key={user.userId || user.userName}
                      className="space-y-2"
                    >
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-semibold text-gray-900">
                          {user.userName}
                        </p>
                        <p className="text-xs font-semibold text-gray-500">
                          {user.completionRate}% concluído
                        </p>
                      </div>
                      <div className="flex items-center justify-between text-[11px] text-gray-500">
                        <span>{user.completedTasks} concluídas</span>
                        <span>{user.totalTasks} atribuídas</span>
                      </div>
                      <div className="h-2 rounded-full bg-gray-100">
                        <div
                          className="h-full rounded-full bg-emerald-500 transition-all duration-300"
                          style={{ width: `${user.completionRate}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-6 text-center text-sm text-gray-500">
                  Nenhum registro de tarefas encontrado neste período.
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="bg-white rounded-3xl border border-gray-100 shadow-md p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-bold text-gray-900">
                      Valores aprovados por instituição financeira
                    </h2>
                    <p className="text-xs text-gray-500">
                      Distribuição dos valores aprovados por I.F. durante o
                      período selecionado
                    </p>
                  </div>
                  <Badge color="pink" className="!bg-[#A60B3E] !text-white">
                    {approvedBanks.length} I.F.
                  </Badge>
                </div>
                <div className="space-y-4">
                  {hasApprovalsData ? (
                    <ReactApexChart
                      options={approvalsOptions}
                      series={approvalsSeries}
                      type="donut"
                      height={280}
                    />
                  ) : approvalsByBank.length ? (
                    <div className="py-10 text-center text-sm text-gray-500">
                      Nenhum valor aprovado registrado no período.
                    </div>
                  ) : (
                    <div className="py-10 text-center text-sm text-gray-500">
                      Nenhuma decisão financeira registrada no período.
                    </div>
                  )}
                  {approvalsByBankLegend.length ? (
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                      {approvalsByBankLegend.map((bank, index) => (
                        <div
                          key={bank.bankId}
                          className="flex items-center justify-between rounded-2xl border border-gray-100 bg-gray-50 px-4 py-3"
                        >
                          <div className="flex items-center gap-3">
                            <span
                              className="h-2.5 w-2.5 rounded-full"
                              style={{
                                backgroundColor: getApprovalPaletteColor(index),
                              }}
                            />
                            <div>
                              <p className="text-sm font-semibold text-gray-900">
                                {bank.bankName}
                              </p>
                              <p className="text-[11px] text-gray-500">
                                {bank.approvedDecisions.length} decisões
                                aprovadas
                              </p>
                            </div>
                          </div>
                          <p className="text-sm font-semibold text-gray-900">
                            {currency(bank.approvedValue)}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="bg-white rounded-3xl border border-gray-100 shadow-md p-6 space-y-4">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">
                    Recusas da Amplia Capital
                  </h2>
                  <p className="text-xs text-gray-500">
                    Rastreie motivos como vontade do cliente ou restrições.
                  </p>
                </div>
                <AmpliaDeclinesPanel
                  banks={amplaDeclineBanks}
                  declineReasons={declineReasons}
                />
              </div>
            </div>
          </>
        )
      ) : null}
      <Modal
        show={isAiAnalysisModalOpen}
        size="4xl"
        onClose={() => setIsAiAnalysisModalOpen(false)}
        className="z-50"
      >
        <ModalHeader className="py-2">
          <span className="text-base mt-1">Análise detalhada por I.A.</span>
        </ModalHeader>
        <ModalBody>
          {aiAnalysisSnapshot ? (
            <div className="space-y-5">
              <div className="rounded-3xl border border-[#A60B3E]/20 bg-gradient-to-br from-[#fff4f8] via-white to-[#eef6ff] p-5">
                <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.3em] text-[#A60B3E]">
                      Relatorio Inteligente
                    </p>
                    <h3 className="text-xl font-bold text-gray-900 mt-1">
                      Análise executiva do período
                    </h3>
                    <p className="text-xs text-gray-600 mt-1">
                      Gerado em {aiAnalysisSnapshot.generatedAt}
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-2 md:min-w-[280px]">
                    <div className="rounded-2xl bg-white/90 border border-white p-3">
                      <p className="text-[10px] uppercase tracking-[0.2em] text-gray-500">
                        Aprovação
                      </p>
                      <p className="text-lg font-semibold text-emerald-600">
                        {aiAnalysisSnapshot.indicators.approvalRate != null
                          ? `${aiAnalysisSnapshot.indicators.approvalRate.toFixed(
                              1,
                            )}%`
                          : "-"}
                      </p>
                    </div>
                    <div className="rounded-2xl bg-white/90 border border-white p-3">
                      <p className="text-[10px] uppercase tracking-[0.2em] text-gray-500">
                        Reprovação
                      </p>
                      <p className="text-lg font-semibold text-rose-600">
                        {aiAnalysisSnapshot.indicators.declineRate != null
                          ? `${aiAnalysisSnapshot.indicators.declineRate.toFixed(
                              1,
                            )}%`
                          : "-"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
                <div className="flex items-center justify-between">
                  <p className="text-[11px] uppercase tracking-[0.3em] text-gray-500">
                    Sinal operacional
                  </p>
                  <Badge color="light" className="!text-gray-700">
                    IA + Indicadores
                  </Badge>
                </div>
                <div className="mt-4 space-y-4">
                  <div>
                    <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                      <span>Taxa de aprovação</span>
                      <span>
                        {aiAnalysisSnapshot.indicators.approvalRate != null
                          ? `${aiAnalysisSnapshot.indicators.approvalRate.toFixed(
                              1,
                            )}%`
                          : "-"}
                      </span>
                    </div>
                    <div className="h-2.5 rounded-full bg-gray-100 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-emerald-500"
                        style={{
                          width: `${Math.max(
                            0,
                            Math.min(
                              100,
                              Number(
                                aiAnalysisSnapshot.indicators.approvalRate ?? 0,
                              ),
                            ),
                          )}%`,
                        }}
                      />
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                      <span>Taxa de reprovação</span>
                      <span>
                        {aiAnalysisSnapshot.indicators.declineRate != null
                          ? `${aiAnalysisSnapshot.indicators.declineRate.toFixed(
                              1,
                            )}%`
                          : "-"}
                      </span>
                    </div>
                    <div className="h-2.5 rounded-full bg-gray-100 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-rose-500"
                        style={{
                          width: `${Math.max(
                            0,
                            Math.min(
                              100,
                              Number(
                                aiAnalysisSnapshot.indicators.declineRate ?? 0,
                              ),
                            ),
                          )}%`,
                        }}
                      />
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                      <span>Conversão de leads</span>
                      <span>
                        {aiAnalysisSnapshot.indicators.conversionRate != null
                          ? `${aiAnalysisSnapshot.indicators.conversionRate.toFixed(
                              1,
                            )}%`
                          : "-"}
                      </span>
                    </div>
                    <div className="h-2.5 rounded-full bg-gray-100 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-[#A60B3E]"
                        style={{
                          width: `${Math.max(
                            0,
                            Math.min(
                              100,
                              Number(
                                aiAnalysisSnapshot.indicators.conversionRate ??
                                  0,
                              ),
                            ),
                          )}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                <div className="rounded-2xl border border-rose-100 bg-rose-50/60 p-4">
                  <p className="text-[11px] uppercase tracking-[0.3em] text-rose-700">
                    Pontos de atenção
                  </p>
                  <div className="mt-3 space-y-3">
                    {aiAnalysisSnapshot.attentionPoints.map((text, index) => (
                      <div
                        key={`attention-${index}`}
                        className="rounded-xl border border-rose-100 bg-white/80 p-3 text-sm text-rose-900"
                      >
                        <p className="text-[10px] uppercase tracking-[0.2em] text-rose-500 mb-1">
                          Alerta {index + 1}
                        </p>
                        <p>{text}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="rounded-2xl border border-emerald-100 bg-emerald-50/60 p-4">
                  <p className="text-[11px] uppercase tracking-[0.3em] text-emerald-700">
                    Recomendações
                  </p>
                  <div className="mt-3 space-y-3">
                    {aiAnalysisSnapshot.recommendations.map((text, index) => (
                      <div
                        key={`rec-${index}`}
                        className="rounded-xl border border-emerald-100 bg-white/80 p-3 text-sm text-emerald-900"
                      >
                        <p className="text-[10px] uppercase tracking-[0.2em] text-emerald-600 mb-1">
                          Ação {index + 1}
                        </p>
                        <p>{text}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-gray-100 bg-gray-50/70 p-4">
                <p className="text-[11px] uppercase tracking-[0.3em] text-gray-500">
                  Resumo executivo
                </p>
                <div className="mt-3 grid grid-cols-1 gap-2">
                  {aiAnalysisSnapshot.executiveSummary.map((text, index) => (
                    <div
                      key={`summary-${index}`}
                      className="rounded-xl border border-gray-100 bg-white p-3 text-sm text-gray-700"
                    >
                      {text}
                    </div>
                  ))}
                </div>
              </div>

              {aiAnalysisSnapshot.slowStages.length ? (
                <div className="rounded-2xl border border-gray-100 bg-white p-4">
                  <p className="text-[11px] uppercase tracking-[0.3em] text-gray-500">
                    Etapas com maior tempo médio
                  </p>
                  <div className="mt-3 space-y-3">
                    {aiAnalysisSnapshot.slowStages.map((stage) => {
                      const maxDays = Math.max(
                        ...aiAnalysisSnapshot.slowStages.map((s) =>
                          Number(s.days ?? 0),
                        ),
                        1,
                      );
                      const percentage = Math.max(
                        8,
                        Math.min(
                          100,
                          (Number(stage.days ?? 0) / maxDays) * 100,
                        ),
                      );

                      return (
                        <div
                          key={`slow-stage-${stage.name}`}
                          className="rounded-xl border border-gray-100 bg-gray-50 p-3"
                        >
                          <div className="flex items-center justify-between text-sm text-gray-700">
                            <span className="font-medium text-gray-900">
                              {stage.name}
                            </span>
                            <span>
                              {stage.days.toFixed(1)} dias · {stage.entries}{" "}
                              negócios
                            </span>
                          </div>
                          <div className="mt-2 h-2 rounded-full bg-gray-200 overflow-hidden">
                            <div
                              className="h-full rounded-full bg-amber-500"
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : null}

              {aiAnalysisError ? (
                <div className="rounded-2xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                  {aiAnalysisError}
                </div>
              ) : null}
            </div>
          ) : (
            <div className="py-8 text-center text-sm text-gray-500">
              Gere a análise para visualizar os insights.
            </div>
          )}
        </ModalBody>
        <ModalFooter className="pt-2 pb-3 flex justify-center">
          <Button color="light" onClick={() => setIsAiAnalysisModalOpen(false)}>
            Fechar
          </Button>
        </ModalFooter>
      </Modal>
      <Modal
        show={isDealsModalOpen}
        size="5xl"
        onClose={() => setIsDealsModalOpen(false)}
        className="z-50"
      >
        <ModalHeader className="py-2">
          <span className="text-base mt-1">Negócios iniciados</span>
        </ModalHeader>
        <ModalBody>
          {dealsStageGroups.groups.length ? (
            <div className="max-h-[420px] overflow-y-hidden">
              <div
                className={
                  dealsStageGroups.groups.length < 3
                    ? "grid gap-4 grid-cols-1 md:grid-cols-2 pr-2 pb-2"
                    : "flex gap-4 overflow-x-auto pr-2 pb-2"
                }
              >
                {dealsStageGroups.groups.map((group) => (
                  <div
                    key={group.stage}
                    className={
                      dealsStageGroups.groups.length < 3
                        ? "w-full rounded-2xl border border-gray-100 bg-gray-50/70 p-4 shadow-sm"
                        : "min-w-[280px] max-w-[320px] flex-1 rounded-2xl border border-gray-100 bg-gray-50/70 p-4 shadow-sm"
                    }
                  >
                    <div className="flex items-center justify-between">
                      <p className="text-[11px] uppercase tracking-[0.3em] text-gray-500">
                        {group.stage}
                      </p>
                      <span className="text-[11px] text-gray-500">
                        ({group.items.length})
                      </span>
                    </div>
                    <div className="mt-3 space-y-2 max-h-[320px] overflow-y-auto pr-1">
                      {group.items.map((deal, index) => (
                        <div
                          key={`${group.stage}-${deal.id ?? index}`}
                          className="rounded-2xl border border-gray-100 bg-white p-3 shadow-sm flex flex-col gap-2"
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div>
                              <p className="text-xs font-semibold text-gray-900">
                                {deal.cardTitle
                                  ? `Card: ${deal.cardTitle}`
                                  : deal.title}
                              </p>
                              {(deal.client || deal.funnel) && (
                                <p className="text-[11px] text-gray-500">
                                  {[deal.client, deal.funnel]
                                    .filter(Boolean)
                                    .join(" · ")}
                                </p>
                              )}
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-semibold text-gray-900">
                                {deal.valueLabel}
                              </p>
                            </div>
                          </div>
                          {deal.dateLabel ? (
                            <p className="text-[11px] text-gray-500">
                              {deal.dateLabel}
                            </p>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="py-10 text-center text-sm text-gray-500">
              Nenhum negócio iniciado registrado nesse período.
            </div>
          )}
          {dealsStageGroups.source === "aggregated" ? (
            <p className="text-[11px] text-gray-500 mt-3">
              Os valores acima usam o agrupamento diário porque os detalhes
              completos vieram pela timeline em vez do feed direto.
            </p>
          ) : null}
        </ModalBody>
        <ModalFooter className="pt-2 pb-3 flex justify-center">
          <Button color="light" onClick={() => setIsDealsModalOpen(false)}>
            Fechar
          </Button>
        </ModalFooter>
      </Modal>
      <Modal
        show={isPipelineModalOpen}
        size="5xl"
        onClose={() => setIsPipelineModalOpen(false)}
        className="z-50"
      >
        <ModalHeader className="py-2">
          <span className="mt-1 text-base">Cards em pipeline</span>
        </ModalHeader>
        <ModalBody>
          {pipelineLoading ? (
            <div className="py-10 text-center text-sm text-gray-500">
              Carregando cards em pipeline…
            </div>
          ) : pipelineError ? (
            <div className="py-10 text-center text-sm text-red-600">
              Não foi possível carregar os cards em pipeline
              {pipelineErrorObj?.message
                ? `: ${pipelineErrorObj.message}`
                : "."}
            </div>
          ) : filteredPipelineCardRows.length ? (
            selectedFunnelIds.length > 2 ? (
              <div className="max-h-[420px] overflow-y-hidden">
                <div className="flex gap-4 overflow-x-scroll pr-2 pb-3">
                  {pipelineFunnelGroups.map((group) => (
                    <div
                      key={group.funnel}
                      className="min-w-[280px] max-w-[320px] flex-1 rounded-2xl border border-gray-100 bg-gray-50/70 p-4 shadow-sm"
                    >
                      <div className="flex items-center justify-between">
                        <p className="text-[11px] uppercase tracking-[0.3em] text-gray-500">
                          {group.funnel}
                        </p>
                        <span className="text-[11px] text-gray-500">
                          ({group.items.length})
                        </span>
                      </div>
                      <div className="mt-3 space-y-2 max-h-[320px] overflow-y-auto pr-1">
                        {group.items.map((card, index) => (
                          <div
                            key={`${group.funnel}-${card.id ?? index}`}
                            className="rounded-2xl border border-gray-100 bg-white p-3 shadow-sm flex flex-col gap-2"
                          >
                            <div className="flex items-start justify-between gap-4">
                              <div>
                                <p className="text-xs font-semibold text-gray-900">
                                  {card.title}
                                </p>
                                {(card.client || card.stage) && (
                                  <p className="text-[11px] text-gray-500">
                                    {[card.client, card.stage]
                                      .filter(Boolean)
                                      .join(" · ")}
                                  </p>
                                )}
                              </div>
                              <div className="text-right">
                                <p className="text-sm font-semibold text-gray-900">
                                  {card.valueLabel}
                                </p>
                              </div>
                            </div>
                            {card.dateLabel ? (
                              <p className="text-[11px] text-gray-500">
                                {card.dateLabel}
                              </p>
                            ) : null}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="max-h-[420px] overflow-y-auto space-y-4 pr-1">
                {pipelineFunnelGroups.map((group) => (
                  <div
                    key={group.funnel}
                    className="rounded-2xl border border-gray-100 bg-gray-50/70 p-4 shadow-sm"
                  >
                    <div className="flex items-center justify-between">
                      <p className="text-[11px] uppercase tracking-[0.3em] text-gray-500">
                        {group.funnel}
                      </p>
                      <span className="text-[11px] text-gray-500">
                        ({group.items.length})
                      </span>
                    </div>
                    <div className="mt-3 space-y-2">
                      {group.items.map((card, index) => (
                        <div
                          key={`${group.funnel}-${card.id ?? index}`}
                          className="rounded-2xl border border-gray-100 bg-white p-3 shadow-sm flex flex-col gap-2"
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div>
                              <p className="text-xs font-semibold text-gray-900">
                                {card.title}
                              </p>
                              {(card.client || card.stage) && (
                                <p className="text-[11px] text-gray-500">
                                  {[card.client, card.stage]
                                    .filter(Boolean)
                                    .join(" · ")}
                                </p>
                              )}
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-semibold text-gray-900">
                                {card.valueLabel}
                              </p>
                            </div>
                          </div>
                          {card.dateLabel ? (
                            <p className="text-[11px] text-gray-500">
                              {card.dateLabel}
                            </p>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : (
            <div className="py-10 text-center text-sm text-gray-500">
              Nenhum card no estágio Pipeline.
            </div>
          )}
        </ModalBody>
        <ModalFooter className="pt-2 pb-3 flex justify-center">
          <Button color="light" onClick={() => setIsPipelineModalOpen(false)}>
            Fechar
          </Button>
        </ModalFooter>
      </Modal>
      <Modal
        show={isRegionModalOpen}
        size="4xl"
        onClose={() => setIsRegionModalOpen(false)}
        className="z-50"
      >
        <ModalHeader className="py-2">
          <span className="mt-1 text-base">
            Clientes do estado {selectedRegionLabel || "—"}
          </span>
        </ModalHeader>
        <ModalBody className="bg-gray-50 pt-2">
          {selectedRegionRows.length ? (
            <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1">
              {selectedRegionRows.map((lead) => (
                <div
                  key={lead.id}
                  className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm flex flex-col gap-2"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-sm font-semibold text-gray-900">
                        {lead.title}
                      </p>
                      {(lead.document || lead.city) && (
                        <p className="text-[11px] text-gray-500">
                          {[lead.document, lead.city]
                            .filter(Boolean)
                            .join(" · ")}
                        </p>
                      )}
                      {(lead.funnel || lead.stage) && (
                        <p className="text-[11px] text-gray-500">
                          {[lead.funnel, lead.stage]
                            .filter(Boolean)
                            .join(" · ")}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-10 text-center text-sm text-gray-500">
              Nenhum cliente encontrado para esse estado.
            </div>
          )}
        </ModalBody>
        <ModalFooter className="pt-2 pb-3 flex justify-center">
          <Button color="light" onClick={() => setIsRegionModalOpen(false)}>
            Fechar
          </Button>
        </ModalFooter>
      </Modal>
      <Modal
        show={isApprovalsModalOpen}
        size="4xl"
        onClose={() => setIsApprovalsModalOpen(false)}
        className="z-50"
      >
        <ModalHeader className="py-2">
          <span className="mt-1 text-base">Valores aprovados</span>
        </ModalHeader>
        <ModalBody>
          {approvedBanks.length ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-gray-900">
                    {currency(kpiApprovedValue)}
                  </p>
                  <p className="text-[11px] text-gray-500">
                    {kpiApprovedCount} decisões · {kpiInstitutionsCount}{" "}
                    instituições
                  </p>
                </div>
                <Badge color="light" className="!text-gray-700">
                  {approvedBanks.length} I.F.
                </Badge>
              </div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {approvedBanks.map((bank, index) => (
                  <div
                    key={bank.bankId || `${bank.bankName}-${index}`}
                    className="rounded-2xl border border-gray-100 bg-gray-50 p-4 space-y-2"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-semibold text-gray-900">
                          {bank.bankName || "Instituição"}
                        </p>
                        <p className="text-[11px] text-gray-500">
                          {bank.approvedDecisions.length} decisões aprovadas
                        </p>
                      </div>
                      <p className="text-sm font-semibold text-emerald-600">
                        {currency(bank.approvedValue)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              <ApprovalDecisionList
                list={approvalDecisionRows}
                institutionsSummary={institutionsSummary}
                maxVisible={approvalDecisionRows.length}
              />
            </div>
          ) : (
            <div className="py-10 text-center text-sm text-gray-500">
              Nenhum valor aprovado registrado no período.
            </div>
          )}
        </ModalBody>
        <ModalFooter className="pt-2 pb-3">
          <Button color="light" onClick={() => setIsApprovalsModalOpen(false)}>
            Fechar
          </Button>
        </ModalFooter>
      </Modal>
      <Modal
        show={isAmpliaDeclinesModalOpen}
        size="4xl"
        onClose={() => setIsAmpliaDeclinesModalOpen(false)}
        className="z-50"
      >
        <ModalHeader className="py-2">
          <div className="mt-1 text-sm md:text-base">
            Declinações da Amplia Capital
          </div>
        </ModalHeader>
        <ModalBody>
          <AmpliaDeclinesPanel
            banks={amplaDeclineBanks}
            declineReasons={declineReasons}
          />
        </ModalBody>
        <ModalFooter className="pt-2 pb-3 flex justify-center">
          <Button
            color="light"
            onClick={() => setIsAmpliaDeclinesModalOpen(false)}
          >
            Fechar
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
